import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Stack,
  Button,
  Skeleton,
  Badge,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TuneIcon from '@mui/icons-material/Tune';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { JOB_FILTER_DEF } from '../panel/JobFilterPanel';
import {
  STATUS_MAP,
  FILTER_META,
  TH_SX,
  TH_HAS_TOT,
  TH_GROUP_SX,
  TH_GROUP2_SX,
  TH_GROUP3_SX,
  TH_GROUP4_SX,
  TH_GROUP5_SX,
  TD_SX,
  REFERENCE_COL_WIDTHS,
  COL_DEFAULTS,
  WORK_OPTIONS,
  FINANCE_OPTIONS,
  BG_USAGE_OPTIONS,
  NO_BG_USAGE_KEY,
} from './projectDashboard/constants';
import {
  getBgColumnValues,
  getPaidValue,
  getExpectedValue,
  getProjectedFund,
  getRefundPendingValue,
  getEstimatedProfit,
  getPoNetOfVat,
  getTargetProfit,
  computeHasCommitment,
} from './projectDashboard/formulas';
import {
  sortProjects,
  fmt,
  toDateInput,
  useDebouncedValue,
} from './projectDashboard/utils';
import { ResizeHandle, NumCell } from './projectDashboard/table/cells';
import ProjectDashboardRow from './projectDashboard/table/ProjectDashboardRow';
import HelpDialog from './projectDashboard/dialogs/HelpDialog';
import FilterPopover from './projectDashboard/dialogs/FilterPopover';
import { useL, useLang } from './projectDashboard/LangContext';
import api from '../../utils/functions/api';
import { matchesProjectPrefix } from '../../utils/functions/projectNumberPrefix';

const AUTO_NUMERIC_COLS = [
  6, 7, 8, 9, 18, 33, 11, 10, 35, 32, 16, 12, 34, 17, 15, 14, 19,
];

// sort เริ่มต้นของตาราง (% ความคืบหน้า → ผู้ว่าจ้าง) — เก็บเป็น reference คงที่ระดับโมดูล
// เพื่อเช็คด้วย === ว่าตอนนี้ยังไม่มีการคลิก sort คอลัมน์ไหนด้วยมือเลย (ดู cycleSortByKey)
const DEFAULT_SORT_CRITERIA = [
  { key: 'progress', dir: 'asc' },
  { key: 'customer', dir: 'asc' },
];


// แถบคั่นก่อนกลุ่มโปรเจกต์ฟ้องร้อง (แยกเป็น component เพราะต้อง useState เก็บตำแหน่งเมาส์ —
// เรียก hook ข้างใน .map() ตรงๆ ไม่ได้) — Tooltip ผูกกับตำแหน่งเมาส์ผ่าน virtual anchorEl แทน
// การผูกกับตัว element เฉยๆ เพราะแถบนี้กว้างเต็มตาราง ถ้าใช้ placement ปกติ tooltip จะลอยอยู่
// กึ่งกลางแถบเสมอ ไม่ตามเมาส์
// "รับเงินหมดแล้ว" = ยอดค้างส่งงานจาก PO (col 9 = PO − รับแล้ว − วางบิลรอรับ) เป็น 0
function isFullyPaid(r) {
  const po = getPoNetOfVat(r);
  const received = (r.received || 0) + (r.seed_received || 0);
  const billed = (r.billed_waiting || 0) + (r.seed_billed_waiting || 0);
  return po > 0 && Math.round((po - received - billed) * 100) <= 0;
}

// กลุ่มลำดับในตาราง: 0 = ปกติ, 1 = รับเงินหมดแล้วแต่ยังมีภาระผูกพัน (เฉพาะตอน toggle ปิด), 2 = ฟ้องร้อง,
// 3 = พิพากษาแล้ว (ล่างสุด — ถ้าติ๊กทั้งฟ้องร้องและพิพากษาแล้ว ถือเป็นกลุ่มพิพากษาแล้ว)
function rowGroup(r, includePaidCommitted) {
  if (r.legal_judged) return 3;
  if (r.legal_lawsuit) return 2;
  if (!includePaidCommitted && isFullyPaid(r) && computeHasCommitment(r)) return 1;
  return 0;
}

function LawsuitDividerRow({ title = 'LAWSUIT LINE', bold = false }) {
  const [pos, setPos] = useState(null);
  return (
    <TableRow>
      <Tooltip
        title={title}
        placement="top"
        open={!!pos}
        PopperProps={{
          anchorEl: pos
            ? { getBoundingClientRect: () => new DOMRect(pos.x, pos.y, 0, 0) }
            : undefined,
        }}
      >
        <TableCell
          colSpan={100}
          onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setPos(null)}
          sx={{
            p: 0,
            height: bold ? '8px' : '4px',
            lineHeight: 0,
            border: 'none',
            bgcolor: bold ? '#000' : '#d1d5db',
            cursor: 'default',
          }}
        />
      </Tooltip>
    </TableRow>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const ProjectDashboardTable = React.forwardRef(function ProjectDashboardTable(
  {
    projects,
    isLoading,
    isAdmin,
    onClickProject,
    onEditProject,
    onDeleteProject,
    searchTerm = '',
    statusFilter = '',
    onStatusFilterChange,
    typeFilter = ['JS'],
    onTypeFilterChange,
    defaultTypeFilter = ['JS'],
    testFilter = false,
    onTestFilterChange,
    statusSummaryChips,
    showGroup1 = false,
    onShowGroup1Change,
    showColNums = false,
    onShowColNumsChange,
    pageScale = 1,
    excludeFilter = new Set(),
    onExcludeFilterChange,
    defaultExcludeFilter = new Set(),
    excludeCounts = {},
    customerFilter: customerFilterProp = '',
    onCustomerFilterChange,
    projectFilter = '',
    onProjectFilterChange,
    onTotalsChange,
    onVisibleIdsChange,
    initialSortCriteria,
    onSortCriteriaChange,
    onSortDefaultChange,
    excludedZeroFields: excludedZeroFieldsProp = new Set(),
    onExcludedZeroFieldsChange,
    excludedProjects: excludedProjectsProp = new Set(),
    onExcludedProjectsChange,
    excludedCustomers: excludedCustomersProp = new Set(),
    onExcludedCustomersChange,
    pinnedProjectId = null,
    onActionRequiredChange,
    includeLawsuitInCashFilter = false,
  },
  ref,
) {
  // IMPORTANT: P5 is a read-only aggregate view.
  // All financial calculations here must follow the same formula source as P111
  // (projectDashboard/formulas.js). P5 should only list projects and summarize totals.
  const L = useL();
  const { lang } = useLang();
  // มือถือ: ล็อกซ้ายแค่ # + Job No (ไม่ล็อกลูกค้า/ชื่อโครงการ/PO ที่รวมกว่า 700px จะกินเต็มจอ), คลิกเดี่ยวรอนานขึ้น
  // เผื่อแตะสองครั้งเปิดโครงการ, ตารางสูงตามจอ — คอมเหมือนเดิมทุกอย่าง
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const containerRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const customerFilter = customerFilterProp;
  const setCustomerFilter = useCallback(
    (v) =>
      onCustomerFilterChange?.(
        typeof v === 'function' ? v(customerFilterProp) : v,
      ),
    [onCustomerFilterChange, customerFilterProp],
  );
  const setProjectFilter = useCallback(
    (v) => onProjectFilterChange?.(v),
    [onProjectFilterChange],
  );
  const clickTimerRef = useRef(null);
  const scheduleClick = useCallback((action) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      action();
      clickTimerRef.current = null;
    }, isMobile ? 400 : 220);
  }, [isMobile]);
  const cancelAndRun = useCallback((action) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    action();
  }, []);
  const [excludedProjects, setExcludedProjects] = useState(
    () => new Set(excludedProjectsProp),
  );
  const [excludedCustomers, setExcludedCustomers] = useState(
    () => new Set(excludedCustomersProp),
  );
  const toggleExcludedProject = useCallback((projectNumber) => {
    setExcludedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectNumber)) next.delete(projectNumber);
      else next.add(projectNumber);
      return next;
    });
  }, []);
  const toggleExcludedCustomer = useCallback((customerName) => {
    setExcludedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(customerName)) next.delete(customerName);
      else next.add(customerName);
      return next;
    });
  }, []);
  // Mo/Yr — เหมือน project/customer: คลิกซ้ายกรองเฉพาะเดือน/ปีนั้น, คลิกขวา exclude ค่านั้นออก
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [excludedMonths, setExcludedMonths] = useState(new Set());
  const [excludedYears, setExcludedYears] = useState(new Set());
  const getRowMonth = useCallback(
    (r) => (r.oldest_job_date ? new Date(r.oldest_job_date).getMonth() + 1 : null),
    [],
  );
  const getRowYear = useCallback(
    (r) => (r.oldest_job_date ? new Date(r.oldest_job_date).getFullYear() : null),
    [],
  );
  const toggleExcludedMonth = useCallback((month) => {
    setExcludedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });
  }, []);
  const toggleExcludedYear = useCallback((year) => {
    setExcludedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  }, []);
  // คลิกขวาที่เซลล์คอลัมน์ตัวเลข/วันที่ — ถ้าค่าปัจจุบันของเซลล์นั้นเป็น 0/ว่างอยู่แล้ว toggle
  // ซ่อนแถวอื่นๆ ที่คอลัมน์เดียวกันเป็น 0/ว่างเหมือนกันทั้งหมด (คลิกขวาที่ค่าไม่ใช่ 0/ว่าง = ไม่มีผล)
  const [excludedZeroFields, setExcludedZeroFields] = useState(
    () => new Set(excludedZeroFieldsProp),
  );
  const toggleZeroExclude = useCallback((key, value) => {
    if ((Number(value) || 0) !== 0) return;
    setExcludedZeroFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const setStatusFilter = (v) => onStatusFilterChange?.(v);
  const setShowGroup1 = (v) =>
    onShowGroup1Change?.(typeof v === 'function' ? v(showGroup1) : v);
  const setShowColNums = (v) =>
    onShowColNumsChange?.(typeof v === 'function' ? v(showColNums) : v);
  const [sortCriteria, setSortCriteria] = useState(
    () => initialSortCriteria || DEFAULT_SORT_CRITERIA,
  );
  const setTypeFilter = (v) =>
    onTypeFilterChange?.(typeof v === 'function' ? v(typeFilter) : v);
  const setTestFilter = (v) =>
    onTestFilterChange?.(typeof v === 'function' ? v(testFilter) : v);
  const [helpOpen, setHelpOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionText, setActionText] = useState('');
  const [actionPassword, setActionPassword] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  const openActionDialog = useCallback((row) => {
    const current = row?.action_required?.text ? row.action_required : null;
    setActionText(current?.text || '');
    setActionPassword('');
    setActionDialog({ row, mode: current ? 'remove' : 'create' });
  }, []);

  const closeActionDialog = useCallback(() => {
    if (!actionSaving) setActionDialog(null);
  }, [actionSaving]);

  const submitActionDialog = useCallback(async () => {
    if (!actionDialog?.row?._id) return;
    const isRemove = actionDialog.mode === 'remove';
    if ((!isRemove && !actionText.trim()) || (isRemove && !actionPassword)) return;
    setActionSaving(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/project/${actionDialog.row._id}/action-required`;
      const response = isRemove
        ? await api.delete(url, { data: { password: actionPassword } })
        : await api.put(url, { text: actionText.trim() });
      onActionRequiredChange?.(
        actionDialog.row._id,
        isRemove ? null : response.data,
      );
      setActionDialog(null);
    } finally {
      setActionSaving(false);
    }
  }, [actionDialog, actionPassword, actionText, onActionRequiredChange]);
  const pageScaleRef = useRef(pageScale);
  useEffect(() => {
    pageScaleRef.current = pageScale;
  }, [pageScale]);

  const [colWidths, setColWidths] = useState({});
  // ความกว้างคอลัมน์เลขงานแบบออโต้ — วัดจากข้อความจริงบนหน้าจอ (ฟอนต์จริง) ของแถวที่แสดงอยู่
  const [jobAutoW, setJobAutoW] = useState(0);
  // กัน click (cycleColSort/cycleProfitSort) ยิงตามหลังตอนลาก resize handle เสร็จ —
  // mouseup หลัง drag ยังตามด้วย click event บน header cell เดิมเสมอ ถ้าไม่กันไว้จะสลับ sort ทุกครั้งที่ลาก
  const resizeJustHappenedRef = useRef(false);

  const startResize = useCallback((colKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.currentTarget.closest('th');
    const startW = th ? th.offsetWidth : COL_DEFAULTS[colKey] || 80;
    let moved = false;
    const onMove = (me) => {
      moved = true;
      setColWidths((prev) => ({
        ...prev,
        [colKey]: Math.max(40, startW + me.clientX - startX),
      }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (moved) {
        resizeJustHappenedRef.current = true;
        setTimeout(() => {
          resizeJustHappenedRef.current = false;
        }, 0);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const rows = useMemo(() => sortProjects(projects?.rows), [projects?.rows]);

  const getCustomerName = useCallback(
    (r) => r.client_id?.name || r.customer || '',
    [],
  );

  const filteredRows = useMemo(() => {
    let r = rows;
    if (projectFilter)
      r = r.filter((row) => row.project_number === projectFilter);
    if (customerFilter)
      r = r.filter((row) =>
        getCustomerName(row)
          .toLowerCase()
          .includes(customerFilter.toLowerCase()),
      );
    if (excludedProjects.size > 0)
      r = r.filter((row) => !excludedProjects.has(row.project_number));
    if (excludedCustomers.size > 0)
      r = r.filter((row) => !excludedCustomers.has(getCustomerName(row)));
    if (monthFilter !== '')
      r = r.filter((row) => getRowMonth(row) === monthFilter);
    if (yearFilter !== '')
      r = r.filter((row) => getRowYear(row) === yearFilter);
    if (excludedMonths.size > 0)
      r = r.filter((row) => !excludedMonths.has(getRowMonth(row)));
    if (excludedYears.size > 0)
      r = r.filter((row) => !excludedYears.has(getRowYear(row)));
    return r;
  }, [
    rows,
    customerFilter,
    projectFilter,
    getCustomerName,
    excludedProjects,
    excludedCustomers,
    monthFilter,
    yearFilter,
    excludedMonths,
    excludedYears,
    getRowMonth,
    getRowYear,
  ]);

  // ตรวจสอบรายวัน — วันที่ล่าสุดที่ตรวจ ใช้คำนวณสีหลอดไฟสถานะ (เขียว/เทา/แดง) ใน ReviewCheckCell
  // ห้าม useMemo([]) ตรงนี้ — ตารางนี้ mount ค้างอยู่นาน (session เปิดยาวข้ามวัน) ถ้า freeze
  // ค่า "วันนี้" ไว้ตอน mount ครั้งแรก จำนวนวันที่ผ่านมาจะคำนวณผิดตลอดจนกว่าจะ F5 หน้าใหม่
  const todayReviewStr = dayjs().format('YYYY-MM-DD');

  // จำนวนคำถามจาก AI ที่รอตอบต่อโครงการ ({ [project_id]: n }) — โชว์ป้าย "AI-n" สีแดงในคอลัมน์ตรวจสอบ
  // โหลดตอนเปิดตาราง และโหลดใหม่เมื่อกลับมาที่แท็บ (ตอบคำถามใน popup แล้วตัวเลขจะลดลง)
  const [aiQuestionCounts, setAiQuestionCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/ai-followup/questions-open-counts`);
        if (!cancelled) setAiQuestionCounts(data || {});
      } catch (e) {
        // ไม่มีป้ายก็ไม่กระทบตาราง
      }
    };
    load();
    window.addEventListener('focus', load);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', load);
    };
  }, []);

  const renderSeedCell = useCallback(({
    field,
    seedValue,
    autoValue,
    alwaysRed = false,
    redOnNegative = true,
    width,
    excludeKey,
  }) => {
    const combined = (Number(autoValue) || 0) + (Number(seedValue) || 0);
    return (
      <TableCell
        key={field}
        sx={{
          ...TD_SX,
          position: 'relative',
          textAlign: 'right',
          userSelect: 'none',
          ...(width !== undefined && { width }),
        }}
        onContextMenu={
          excludeKey
            ? (e) => {
                e.preventDefault();
                toggleZeroExclude(excludeKey, combined);
              }
            : undefined
        }
      >
        <Box sx={{ textAlign: 'right' }}>
          <NumCell
            value={combined}
            redWhenNegative={redOnNegative}
            color={alwaysRed ? 'error.main' : undefined}
          />
        </Box>
      </TableCell>
    );
  }, [toggleZeroExclude]);

  const getVal = useCallback((row, field) => row[field], []);

  const customers = useMemo(() => {
    return [
      ...new Set(rows.map((r) => getCustomerName(r)).filter(Boolean)),
    ].sort();
  }, [rows, getCustomerName]);

  const effectiveColWidths = useMemo(() => {
    const base = { ...COL_DEFAULTS };
    if (!rows.length) return base;

    const canvas =
      typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas ? canvas.getContext('2d') : null;
    if (ctx) {
      // NumCell/InlineAmountCell ใช้ variant caption + fontSize ประมาณ 0.75rem
      ctx.font = '400 12px Roboto, Helvetica, Arial, sans-serif';
    }
    const measure = (text) => {
      const s = String(text ?? '');
      if (!ctx) return s.length * 7;
      return Math.ceil(ctx.measureText(s).width);
    };

    const maxTextPx = Object.fromEntries(AUTO_NUMERIC_COLS.map((c) => [c, 0]));
    let maxCustomerTextPx = measure(L('ผู้ว่าจ้าง', 'Customer'));
    let totalEstimatedProfit = 0;

    rows.forEach((row) => {
      const merged = row;
      const po = getPoNetOfVat(row);
      const receivedCombined =
        (row.received ?? 0) + (getVal(row, 'seed_received') ?? 0);
      const billedCombined =
        (row.billed_waiting ?? 0) + (getVal(row, 'seed_billed_waiting') ?? 0);
      const col9 = po - receivedCombined - billedCombined;

      const { col14, col15, col16, col17, col12 } = getBgColumnValues(merged);
      const projectedFund = getProjectedFund(merged);
      const realprofit = getEstimatedProfit(merged);
      totalEstimatedProfit += realprofit;
      maxCustomerTextPx = Math.max(
        maxCustomerTextPx,
        measure(getCustomerName(row)),
      );

      const textByCol = {
        6: fmt(po),
        7: fmt(billedCombined),
        8: fmt(receivedCombined),
        9: fmt(col9),
        18: fmt(getTargetProfit(row)),
        33: fmt(row.total_budget || 0),
        11: fmt(getPaidValue(row)),
        10: fmt(projectedFund),
        35: fmt(getExpectedValue(row)),
        32: fmt(row.ap_pending_unpaid || 0),
        16: fmt(col16),
        12: fmt(col12),
        34: fmt(getRefundPendingValue(row)),
        17: fmt(col17),
        15: fmt(col15),
        14: col14 !== null ? fmt(col14) : '—',
        19: fmt(realprofit),
      };

      AUTO_NUMERIC_COLS.forEach((col) => {
        maxTextPx[col] = Math.max(maxTextPx[col], measure(textByCol[col]));
      });
    });

    AUTO_NUMERIC_COLS.forEach((col) => {
      // เพิ่มเผื่อความกว้างอีก 1 เท่าจากค่าปัจจุบัน: รวม +60
      base[col] = Math.max(
        40,
        maxTextPx[col] + (col === 19 ? 24 : 60),
      );
    });

    // กำไรประมาณการต้องพอดีกับทั้งค่ารายแถวและยอดรวม แต่ไม่เผื่อพื้นที่ตามหัวข้อ
    // (หัวข้อขึ้นบรรทัดได้) จึงช่วยลดความกว้างรวมของตารางโดยไม่ตัดตัวเลข
    base[19] = Math.max(
      base[19],
      measure(fmt(totalEstimatedProfit)) + 24,
    );

    // Customer กว้างตามชื่อจริง สูงสุด 250px; ชื่อที่เกินใช้ ellipsis + tooltip
    base[5] = Math.min(
      COL_DEFAULTS[5],
      Math.max(80, maxCustomerTextPx + 24),
    );

    return base;
  }, [rows, getVal, getCustomerName, L]);

  // getW: user resize (colWidths) can only go WIDER than content-required width, never narrower
  const getW = useCallback(
    (col) => {
      const width = Math.max(
        colWidths[col] || 0,
        effectiveColWidths[col] || COL_DEFAULTS[col] || 80,
        col === 1 ? jobAutoW : 0,
      );
      return col === 5 ? Math.min(width, COL_DEFAULTS[5]) : width;
    },
    [colWidths, effectiveColWidths, jobAutoW],
  );

  // ความกว้างรวมจริงของตาราง (ผลรวม <col> ทุกอันที่แสดงอยู่ตอนนี้) — ต้องคำนวณสดแล้วใส่เป็น minWidth ของ
  // <Table> เอง เพราะ MUI Table + table-layout:fixed จะบีบทุกคอลัมน์ให้พอดีกับความกว้างของตัว <Table>
  // เสมอ (ของเดิม hardcode minWidth:1500 ไว้ตายตัว พอเพิ่มความกว้างคอลัมน์เยอะขึ้นจนผลรวมเกิน 1500
  // ตารางเลยบีบทุกคอลัมน์ให้แคบลงทับ getW() ที่ตั้งไว้หมด ทั้งที่ getW() คำนวณถูกแล้วก็ตาม)
  const showCol2 = true;
  const tableMinWidth = useMemo(() => {
    const moneyGroupCols = [
      11, 10, 17, 15, 14, 19, 13, 20,
    ];
    const fixedCols = showCol2 ? [1, 2, 36, 3, 5, 4, 6] : [1, 36, 3, 5, 4, 6];
    let total = 38 + fixedCols.reduce((a, i) => a + getW(i), 0);
    total += getW(9); // ยอดค้างส่งงานจาก PO (badge 4) — แสดงเสมอ ไม่ผูกกับ toggle กลุ่มไหนแล้ว
    total += getW(7); // วางบิลแล้วรอรับเงิน — แสดงเสมอ
    if (showGroup1)
      total += [8, 18, 33, 37, 38, 35, 32, 16, 12, 34, 27, 28, 31, 29, 30].reduce(
        (a, i) => a + getW(i),
        0,
      );
    total += moneyGroupCols.reduce((a, i) => a + getW(i), 0);
    total += getW(26);
    return total;
  }, [getW, showGroup1]);

  const stickyLeftOf = useMemo(() => {
    const result = { 0: 0 };
    let offset = 38;
    // ล็อกถาวรเฉพาะ #, Job No., Customer, Project และ PO
    for (const i of (isMobile ? [1] : [1, 5, 4, 6])) {
      result[i] = offset;
      offset += getW(i);
    }
    return result;
  }, [getW, isMobile]);

  const frzTh = (col) =>
    stickyLeftOf[col] !== undefined
      ? { position: 'sticky', left: stickyLeftOf[col], zIndex: 6 }
      : {};

  const onContainerMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (
      e.target.closest(
        'button, input, a, [role="button"], [class*="MuiIconButton"]',
      )
    )
      return;
    const el = containerRef.current;
    if (!el) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = el.scrollLeft;
    const startTop = el.scrollTop;
    let moved = false;
    const onMove = (me) => {
      const dx = me.clientX - startX;
      const dy = me.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      if (moved) {
        el.scrollLeft = startLeft - dx / pageScaleRef.current;
        el.scrollTop = startTop - dy / pageScaleRef.current;
        me.preventDefault();
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setIsDragging(false);
    };
    setIsDragging(true);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // คลิก header คอลัมน์ที่ยังไม่เคยกด = เพิ่มเป็นเงื่อนไข sort ด้วยมือ (สูงสุด 2 เงื่อนไขพร้อมกัน)
  // ถ้ากดคอลัมน์ที่ 3 ขึ้นไป จะเคลียร์ 2 เงื่อนไขเดิมทิ้งแล้วเริ่มนับใหม่จากคอลัมน์นั้น (ไม่ใช่พ่วงไม่จำกัด)
  // ถ้ากดจนเอาเงื่อนไขที่กดเองออกหมด (cycle asc→desc→ไม่ sort) ให้เหลือ [] (ไม่ sort เลย, เรียงตามลำดับ
  // ธรรมชาติ) — ห้าม fallback กลับไปใช้ DEFAULT_SORT_CRITERIA เด็ดขาด เพราะ progress/customer เป็น
  // ส่วนหนึ่งของ default เอง ถ้า fallback กลับไปจะเหมือนปลดล็อคไม่ได้ (เด้งไปโชว์ว่าอีกคอลัมน์ sort อยู่แทน
  // ทั้งที่ user ไม่ได้กด) — เกิดปัญหานี้มาแล้ว 2 รอบ อย่ากลับไปใช้ fallback อีก
  const cycleSortByKey = useCallback((key) => {
    if (resizeJustHappenedRef.current) return;
    setSortCriteria((prev) => {
      const base = prev === DEFAULT_SORT_CRITERIA ? [] : prev;
      const found = base.find((s) => s.key === key);
      const currentDir = found?.dir || null;
      const nextDir =
        currentDir === null ? 'desc' : currentDir === 'desc' ? 'asc' : null;

      let remaining = base.filter((s) => s.key !== key);
      if (!found && remaining.length >= 2) remaining = [];

      return nextDir ? [...remaining, { key, dir: nextDir }] : remaining;
    });
  }, []);

  // คลิก header ที่กำลัง exclude แบบ 0/ว่างอยู่ = เคลียร์ exclude นั้นออกก่อน (ไม่ไป cycle sort
  // ทับซ้อนในคลิกเดียวกัน) — ครอบคลุมทุกคอลัมน์เพราะ header ทั้งหมดเรียก handleHeaderSortClick นี้
  const handleHeaderSortClick = useCallback(
    (key) => {
      if (excludedZeroFields.has(key)) {
        setExcludedZeroFields((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        return;
      }
      cycleSortByKey(key);
    },
    [cycleSortByKey, excludedZeroFields],
  );

  // กด ESC ในหน้า P5 — ทำเป็นขั้นบันไดวนไม่รู้จบ ประเมินใหม่ทุกครั้งที่กด (ไม่ใช่นับจำนวนครั้งที่กด):
  // 1) มีฟิลเตอร์ในตารางค้างอยู่ (customer/project/month/year filter + exclude ต่างๆ รวม
  //    excludedZeroFields) หรือการซ่อนคอลัมน์ไม่ตรง default (showGroup1) →
  //    เคลียร์/คืนค่า default ทั้งหมดในคลิกเดียว จบการกดครั้งนี้ (ไม่ไปแตะ sort ในกดครั้งเดียวกัน)
  //    ถ้าตรง default หมดแล้วตั้งแต่แรก → ข้ามไปข้อ 2 ทันทีในการกดครั้งเดียวกันนี้เลย
  // 2) sortCriteria ยังไม่ว่าง (ไม่ว่าจะเป็น DEFAULT_SORT_CRITERIA หรือคอลัมน์ที่ user กดเอง) → เคลียร์เป็น []
  // 3) sortCriteria ว่างอยู่ → คืนเป็น DEFAULT_SORT_CRITERIA (ค่าตอนเข้าหน้ามาครั้งแรก)
  // ข้อ 2/3 นี้คือการ "restore default" ที่ user สั่งชัดเจนผ่าน ESC เท่านั้น ไม่ใช่ fallback อัตโนมัติ
  // แบบที่ cycleSortByKey ห้ามไว้ด้านบน (คนละ trigger กัน) — ถ้ามี popup/dialog/popover (MUI) เปิดอยู่
  // ให้ ESC ปิดของนั้นตามปกติก่อน ไม่ยุ่งกับ cascade นี้เลย
  useEffect(() => {
    const handleEscKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable) return;
      if (document.querySelector('.MuiModal-root, .MuiPopover-root, .MuiMenu-root')) return;

      const hasActiveFilter =
        excludedProjects.size > 0 ||
        excludedCustomers.size > 0 ||
        !!customerFilter ||
        !!projectFilter ||
        monthFilter !== '' ||
        yearFilter !== '' ||
        excludedMonths.size > 0 ||
        excludedYears.size > 0 ||
        excludedZeroFields.size > 0 ||
        showGroup1 !== false;

      if (hasActiveFilter) {
        setExcludedProjects(new Set());
        setExcludedCustomers(new Set());
        setCustomerFilter('');
        setProjectFilter('');
        setMonthFilter('');
        setYearFilter('');
        setExcludedMonths(new Set());
        setExcludedYears(new Set());
        setExcludedZeroFields(new Set());
        setShowGroup1(false);
        return;
      }

      setSortCriteria((prev) => (prev.length > 0 ? [] : DEFAULT_SORT_CRITERIA));
    };
    window.addEventListener('keydown', handleEscKeyDown);
    return () => window.removeEventListener('keydown', handleEscKeyDown);
  }, [
    excludedProjects,
    excludedCustomers,
    customerFilter,
    projectFilter,
    monthFilter,
    yearFilter,
    excludedMonths,
    excludedYears,
    excludedZeroFields,
    showGroup1,
    setCustomerFilter,
    setProjectFilter,
  ]);

  const getSortDir = useCallback(
    (key) => sortCriteria.find((s) => s.key === key)?.dir || null,
    [sortCriteria],
  );

  // ไอคอน sort ใช้ position:absolute เสมอ — ไม่งั้นตอนขึ้น (fontSize 36) จะไปดันความสูงของ header
  // ทั้งแถวให้สูงขึ้นทุกคอลัมน์ (TableCell ในแถวเดียวกันสูงเท่ากันหมด) แค่เพราะคอลัมน์เดียวถูก sort อยู่
  const SORT_ICON_SX = {
    fontSize: 18,
    color: '#ef4444',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
  };
  const sortIcon = (key) =>
    getSortDir(key) ? (
      getSortDir(key) === 'asc' ? (
        <ArrowDropUpIcon sx={SORT_ICON_SX} />
      ) : (
        <ArrowDropDownIcon sx={SORT_ICON_SX} />
      )
    ) : null;

  const thSortSx = (key, base, hoverBg = '#7dd3fc') => ({
    ...base,
    cursor: 'pointer',
    userSelect: 'none',
    ...(getSortDir(key) && {
      bgcolor: '#c09614',
      color: '#fff',
      borderBottom: '3px solid #d4a017',
    }),
    // ไม่รวมแถวที่ค่า=0/ว่างของคอลัมน์นี้อยู่ — ทับสี sort เดิม (ถ้ามี) ด้วยม่วงเข้มเดียวกับ
    // header โครงการ/ผู้ว่าจ้างตอนถูก exclude เพื่อให้เห็นชัดว่าคอลัมน์ไหนกำลังกรองอยู่
    ...(excludedZeroFields.has(key) && {
      bgcolor: '#7c3aed',
      color: '#fff',
      borderBottom: 'none',
    }),
    '&:hover': {
      bgcolor: excludedZeroFields.has(key)
        ? '#6d28d9'
        : getSortDir(key)
        ? '#9b780f'
        : hoverBg,
    },
  });

  // จำลองสูตร "รับเงิน วันที่" (col29) จาก ProjectDashboardRow.js ให้ตรงกันเป๊ะ — ใช้เฉพาะ
  // ตอน sort เพราะแถวจริงยังคำนวณในไฟล์นั้นแยกต่างหาก
  const computeExpectedPaymentDate = useCallback(
    (row) => {
      const deliveryDate = getVal(row, 'delivery_date');
      const creditDays = row.client_id?.credit_days;
      const paymentCycleDay = getVal(row, 'payment_cycle_day');
      if (!deliveryDate || creditDays == null || creditDays === '')
        return null;
      const base = new Date(deliveryDate);
      base.setDate(base.getDate() + Number(creditDays));
      if (paymentCycleDay == null || paymentCycleDay === '') return base;
      const day = Number(paymentCycleDay);
      const sameMonth = new Date(base.getFullYear(), base.getMonth(), day);
      return sameMonth >= base
        ? sameMonth
        : new Date(base.getFullYear(), base.getMonth() + 1, day);
    },
    [getVal],
  );

  // ค่าตัวเลขของคอลัมน์ตาม key เดียว (ใช้ทั้ง sort และตัวกรอง "ไม่รวมแถวที่ค่า=0/ว่าง" ที่คลิกขวา
  // จากเซลล์ในตาราง) — คีย์ที่ไม่รู้จัก (project_number/name/customer/realprofit ฯลฯ ที่เทียบแบบ
  // ข้อความ/ไม่มีสูตรตัวเลขเดี่ยว) คืนค่า null
  const getFieldValue = useCallback(
    (key, row) => {
      const merged = row;
      switch (key) {
        case 'po':
          return getPoNetOfVat(row);
        case 'received':
          return (row.received ?? 0) + (row.seed_received ?? 0);
        case 'billed_waiting':
          return (row.billed_waiting ?? 0) + (row.seed_billed_waiting ?? 0);
        case 'po_remaining':
          return (
            getPoNetOfVat(row) -
            (row.received ?? 0) -
            (row.seed_received ?? 0) -
            (row.billed_waiting ?? 0) -
            (row.seed_billed_waiting ?? 0)
          );
        case 'progress':
          return Number(row.progress) || 0;
        case 'target_profit':
          return getTargetProfit(row);
        case 'total_budget':
          return row.total_budget ?? 0;
        case 'projected_fund':
          return getProjectedFund(row);
        case 'total_expenses':
          return getPaidValue(row);
        case 'col12_payable_bg_eligible':
          return getBgColumnValues(merged).col12;
        case 'expected_value':
          return getExpectedValue(row);
        case 'ap_pending_unpaid':
          return row.ap_pending_unpaid ?? 0;
        case 'bg_used_pct':
          return row.bg_used_pct ?? 0;
        case 'over_budget':
          return getBgColumnValues(merged).col14 ?? 0;
        case 'net_remaining':
          return getBgColumnValues(row).col15;
        case 'refund_pending':
          return getRefundPendingValue(row);
        case 'to_spend':
          return row.to_spend ?? 0;
        case 'col17':
          return getBgColumnValues(merged).col17;
        case 'oldest_job_month':
          return row.oldest_job_date ? new Date(row.oldest_job_date).getMonth() + 1 : 0;
        case 'oldest_job_year':
          return row.oldest_job_date ? new Date(row.oldest_job_date).getFullYear() : 0;
        case 'realprofit':
          return getEstimatedProfit(merged);
        case 'profit_pct': {
          const po = getPoNetOfVat(row);
          return po > 0 ? (getEstimatedProfit(merged) / po) * 100 : 0;
        }
        case 'delivery_date': {
          const d = getVal(row, 'delivery_date');
          return d ? new Date(d).getTime() : 0;
        }
        case 'credit_days':
          return row.client_id?.credit_days ?? 0;
        case 'payment_cycle_day': {
          const p = getVal(row, 'payment_cycle_day');
          return p != null && p !== '' ? Number(p) : 0;
        }
        case 'expected_date': {
          const e = computeExpectedPaymentDate(row);
          return e ? e.getTime() : 0;
        }
        case 'days_left': {
          const e = computeExpectedPaymentDate(row);
          if (!e) return 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return Math.round((e.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        }
        default:
          return null;
      }
    },
    [getVal, computeExpectedPaymentDate],
  );

  const compareRowsBySortKey = useCallback(
    (key, a, b) => {
      if (key === 'project_number') {
        return (a.project_number || '').localeCompare(
          b.project_number || '',
          'th',
          { numeric: true },
        );
      }
      if (key === 'customer') {
        return getCustomerName(a).localeCompare(getCustomerName(b), 'th');
      }
      if (key === 'name') {
        return (a.name || '').localeCompare(b.name || '', 'th');
      }

      const valA = getFieldValue(key, a);
      const valB = getFieldValue(key, b);
      if (valA === null || valB === null) return 0;
      return (Number(valA) || 0) - (Number(valB) || 0);
    },
    [getCustomerName, getFieldValue],
  );

  // ── Perceived perf: debounce searchTerm so typing stays responsive ──────────
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 180);

  // ── Actual perf: filter+sort only recalculates when inputs truly change ──────
  // toggle: รวมโครงการที่รับเงินหมดแล้วแต่ยังมีภาระผูกพัน (ยอดค้างส่งงานจาก PO = 0) ไหม
  // ปิด (default) = แยกเป็นกลุ่มพื้นฟ้า มีเส้นคั่น และไม่นับในยอดรวมหัวตาราง
  // เปิด = เรียงปนกับโครงการอื่นตามปกติ ไม่มีเส้นคั่น และนับในยอดรวม
  const [includeCompletedCommitted, setIncludeCompletedCommitted] =
    useState(false);

  const displayRows = useMemo(() => {
    let result = filteredRows;

    if (debouncedSearchTerm) {
      const q = debouncedSearchTerm.toLowerCase();
      result = result.filter((r) => {
        return (
          (r.project_number || '').toLowerCase().includes(q) ||
          (r.name || '').toLowerCase().includes(q) ||
          (r.client_id?.name || '').toLowerCase().includes(q) ||
          String(r.cost || '').includes(q) ||
          (r.dashboard_note ?? '')
            .toString()
            .toLowerCase()
            .includes(q)
        );
      });
    }

    if (typeFilter.length || testFilter) {
      result = result.filter(
        (r) => matchesProjectPrefix(r.project_number, typeFilter)
          || (testFilter && /^TEST/i.test(r.project_number || '')),
      );
    }

    if (statusFilter) {
      result = result.filter((r) => {
        const ws = r.work_status || '';
        const fs = r.finance_status || '';
        const bgUsage = r.bg_usage_category || '';
        const lw = Boolean(r.legal_warranty);
        const ld = Boolean(r.legal_debt);
        const ll = Boolean(r.legal_lawsuit);
        const rs = r.report_status || '';
        // ยกเลิก (work_status='cancelled') = ไม่มีภาระผูกพันอีกต่อไป ไม่ว่าสถานะอื่นจะเป็นอะไร
        const hasCom =
          ws !== 'cancelled' &&
          (ws !== 'done' ||
            fs !== 'completed_po' ||
            lw ||
            ld ||
            ll ||
            rs === 'pending');
        if (statusFilter === 'has_commitment') return hasCom;
        if (statusFilter === 'no_commitment') return !hasCom;
        if (statusFilter === 'work_waiting') return !ws || ws === 'waiting';
        if (statusFilter === 'work_in_progress') return ws === 'in_progress';
        if (statusFilter === 'work_done') return ws === 'done';
        if (statusFilter === 'work_cancelled') return ws === 'cancelled';
        if (statusFilter === 'finance_none') return !fs || fs === 'none';
        if (statusFilter === 'finance_partial') return fs === 'partial';
        if (statusFilter === 'finance_billed_waiting')
          return fs === 'billed_waiting';
        if (statusFilter === 'finance_last_installment')
          return fs === 'last_installment';
        if (statusFilter === 'finance_completed_po')
          return fs === 'completed_po';
        if (statusFilter === 'bg_no_bg_usage')
          return bgUsage === NO_BG_USAGE_KEY;
        if (statusFilter === 'legal_warranty') return lw;
        if (statusFilter === 'legal_debt') return ld;
        if (statusFilter === 'legal_lawsuit') return ll;
        if (statusFilter === 'col9_positive') {
          const merged = r;
          const isJs = matchesProjectPrefix(merged.project_number, defaultTypeFilter);
          const mws = merged.work_status || '';
          const mfs = merged.finance_status || '';
          const hasCom =
            mws !== 'cancelled' &&
            (mws !== 'done' ||
              mfs !== 'completed_po' ||
              merged.legal_warranty ||
              merged.legal_debt ||
              merged.legal_lawsuit ||
              merged.report_status === 'pending');
          const po = getPoNetOfVat(merged);
          const col9 =
            po -
            (merged.received || 0) -
            (merged.seed_received || 0) -
            (merged.billed_waiting || 0) -
            (merged.seed_billed_waiting || 0);
          return isJs && hasCom && Math.round(col9 * 100) > 0;
        }
        if (statusFilter === 'col7_positive') {
          const merged = r;
          const isJs = matchesProjectPrefix(merged.project_number, defaultTypeFilter);
          const mws = merged.work_status || '';
          const mfs = merged.finance_status || '';
          const hasCom =
            mws !== 'cancelled' &&
            (mws !== 'done' ||
              mfs !== 'completed_po' ||
              merged.legal_warranty ||
              merged.legal_debt ||
              merged.legal_lawsuit ||
              merged.report_status === 'pending');
          const col7 =
            (merged.billed_waiting || 0) + (merged.seed_billed_waiting || 0);
          // ให้ตรงกับการ์ด "วางบิลแล้ว" — ไม่รวมโครงการฟ้องร้อง/พิพากษาแล้ว
          return (
            isJs &&
            hasCom &&
            !merged.legal_lawsuit &&
            !merged.legal_judged &&
            Math.round(col7 * 100) > 0
          );
        }
        if (statusFilter === 'col9_or_col7_nonzero') {
          // คลิกซ้ายที่การ์ด "Total Cash In" — โชว์เฉพาะแถวที่ PO Remain (col9) ไม่เท่ากับ 0
          // "หรือ" Invoiced (col7) ไม่เท่ากับ 0 (อย่างใดอย่างหนึ่งพอ ไม่ต้องทั้งคู่ — user ยืนยัน OR
          // ไม่ใช่ AND) เช็ค != 0 ไม่ใช่แค่ > 0 เหมือน col9/col7_positive เดี่ยวๆ ด้านบน เพราะ user
          // ระบุ "ไม่เท่ากับ 0" ชัดเจน — ยังคง isJs/hasCom ไว้เหมือนปุ่มอื่นในแถวเดียวกันเพื่อความสม่ำเสมอ
          const merged = r;
          const isJs = matchesProjectPrefix(merged.project_number, defaultTypeFilter);
          const mws = merged.work_status || '';
          const mfs = merged.finance_status || '';
          const hasCom =
            mws !== 'cancelled' &&
            (mws !== 'done' ||
              mfs !== 'completed_po' ||
              merged.legal_warranty ||
              merged.legal_debt ||
              merged.legal_lawsuit ||
              merged.report_status === 'pending');
          const po = getPoNetOfVat(merged);
          const col9 =
            po -
            (merged.received || 0) -
            (merged.seed_received || 0) -
            (merged.billed_waiting || 0) -
            (merged.seed_billed_waiting || 0);
          const col7 =
            (merged.billed_waiting || 0) + (merged.seed_billed_waiting || 0);
          // โชว์เฉพาะแถวที่มีค่าถูกนำไปรวมในการ์ด "เงินสดรับรวม" จริง:
          // เงินสดรับรวม = (ยอดค้างส่งงาน col9 − สรุปอนาคตต้องจ่าย col17) + วางบิลแล้ว col7
          // ฟ้องร้อง/พิพากษาแล้ว ไม่ถูกนับ ยกเว้นติ๊ก "รวมยอดค้างจ่ายในงานที่ฟ้องร้อง" (นับแค่ col17 ของมัน)
          const col17 = getBgColumnValues(merged).col17 || 0;
          const hasCol17 = Math.round(col17 * 100) !== 0;
          if (merged.legal_lawsuit || merged.legal_judged) {
            return isJs && hasCom && includeLawsuitInCashFilter && hasCol17;
          }
          return (
            isJs &&
            hasCom &&
            (Math.round(col9 * 100) !== 0 ||
              Math.round(col7 * 100) !== 0 ||
              hasCol17)
          );
        }
        return true;
      });
    }

    if (excludeFilter.size > 0) {
      const excludeKeys = [...excludeFilter];
      result = result.filter((r) => {
        const ws = r.work_status || '';
        const fs = r.finance_status || '';
        const bgUsage = r.bg_usage_category || '';
        const lw = Boolean(r.legal_warranty);
        const ld = Boolean(r.legal_debt);
        const ll = Boolean(r.legal_lawsuit);
        const rs = r.report_status || '';
        // ยกเลิก (work_status='cancelled') = ไม่มีภาระผูกพันอีกต่อไป ไม่ว่าสถานะอื่นจะเป็นอะไร
        const hasCom =
          ws !== 'cancelled' &&
          (ws !== 'done' ||
            fs !== 'completed_po' ||
            lw ||
            ld ||
            ll ||
            rs === 'pending');
        const matchKey = (key) => {
          if (key === 'has_commitment') return hasCom;
          if (key === 'no_commitment') return !hasCom;
          if (key === 'work_waiting') return !ws || ws === 'waiting';
          if (key === 'work_in_progress') return ws === 'in_progress';
          if (key === 'work_done') return ws === 'done';
          if (key === 'work_cancelled') return ws === 'cancelled';
          if (key === 'finance_none') return !fs || fs === 'none';
          if (key === 'finance_partial') return fs === 'partial';
          if (key === 'finance_billed_waiting') return fs === 'billed_waiting';
          if (key === 'finance_last_installment')
            return fs === 'last_installment';
          if (key === 'finance_completed_po') return fs === 'completed_po';
          if (key === 'bg_no_bg_usage') return bgUsage === NO_BG_USAGE_KEY;
          // งาน/การเงินยังไม่เสร็จ = ยังมีภาระผูกพันจริงเสมอ ห้าม exclude ออกแค่เพราะมีบันทึก
          // นิติกรรมไว้ (Warranty/เจ้าหนี้/ฟ้องร้อง) — exclude ได้เฉพาะกรณีงาน+เงินจบครบแล้วจริงๆ
          const doneAndPaid = ws === 'done' && fs === 'completed_po';
          if (key === 'legal_warranty') return lw && doneAndPaid;
          if (key === 'legal_debt') return ld && doneAndPaid;
          // ฟ้องร้องไม่ถูก exclude โดย default (แสดงเสมอ) — ถ้า user สั่งเอาออกเองให้ซ่อนทุกโครงการที่ฟ้องร้อง ไม่ว่างาน/การเงินจะเสร็จหรือยัง
          if (key === 'legal_lawsuit') return ll;
          if (key === 'col9_positive') {
            const merged = r;
            const po = getPoNetOfVat(merged);
            const col9 =
              po -
              (merged.received || 0) -
              (merged.seed_received || 0) -
              (merged.billed_waiting || 0) -
              (merged.seed_billed_waiting || 0);
            return Math.round(col9 * 100) > 0;
          }
          if (key === 'col7_positive') {
            const merged = r;
            return (
              Math.round(
                ((merged.billed_waiting || 0) +
                  (merged.seed_billed_waiting || 0)) *
                  100,
              ) > 0
            );
          }
          return false;
        };
        return !excludeKeys.some(matchKey);
      });
    }

    if (excludedZeroFields.size > 0) {
      const zeroKeys = [...excludedZeroFields];
      result = result.filter(
        (r) => !zeroKeys.some((key) => (Number(getFieldValue(key, r)) || 0) === 0),
      );
    }

    // โปรเจกต์ฟ้องร้อง (legal_lawsuit) ต้องอยู่ล่างสุดของตารางเสมอ ไม่ว่าจะ sort คอลัมน์ไหน/มี
    // sortCriteria หรือไม่ก็ตาม — เทียบสถานะฟ้องร้องก่อนเป็นอันดับแรก แล้วค่อย sort ตาม sortCriteria/
    // ลำดับ default ปกติเฉพาะภายในกลุ่มเดียวกัน (ฟ้องร้อง vs ไม่ฟ้องร้อง)
    result = [...result].sort((a, b) => {
      const aGroup = rowGroup(a, includeCompletedCommitted);
      const bGroup = rowGroup(b, includeCompletedCommitted);
      if (aGroup !== bGroup) return aGroup - bGroup;
      for (const rule of sortCriteria) {
        const diff = compareRowsBySortKey(rule.key, a, b);
        if (diff !== 0) return rule.dir === 'asc' ? diff : -diff;
      }
      return (a.project_number || '').localeCompare(
        b.project_number || '',
        'th',
        { numeric: true },
      );
    });

    // แถวที่ pin ไว้ (โปรเจกต์ที่กำลังเปิด popup P111 อยู่ตอนนี้) — กันหลุดออกจากตารางกลางอากาศ
    // ถ้า user แก้ field ที่มีผลกับตัวกรองที่เปิดอยู่ (เช่น แก้สถานะจนหลุดเงื่อนไข) ระหว่างเปิด popup
    // ค้างไว้ ต่อท้ายด้วยเสมอไม่ว่าจะหลุดตัวกรอง/ค้นหา/exclude ข้อไหนก็ตาม
    if (
      pinnedProjectId &&
      !result.some((r) => r._id === pinnedProjectId)
    ) {
      const pinnedRow = rows.find((r) => r._id === pinnedProjectId);
      if (pinnedRow) result = [pinnedRow, ...result];
    }

    return result;
  }, [
    filteredRows,
    debouncedSearchTerm,
    typeFilter,
    testFilter,
    statusFilter,
    sortCriteria,
    getVal,
    compareRowsBySortKey,
    excludeFilter,
    excludedZeroFields,
    getFieldValue,
    pinnedProjectId,
    rows,
    includeCompletedCommitted,
    includeLawsuitInCashFilter,
  ]);

  // คอลัมน์เลขงานออโต้: วัดความกว้างจริงของรหัสโครงการที่แสดงอยู่ (ฟอนต์จริงของหน้าจอ ไม่ใช่ค่าประมาณ)
  // + padding ซ้าย/ขวา 16px + เผื่อ 4px — stickyLeftOf/tableMinWidth ใช้ getW(1) ต่อให้เอง จึงไม่ทับคอลัมน์อื่น
  useLayoutEffect(() => {
    const measureJob = () => {
      const nodes = containerRef.current?.querySelectorAll('[data-job-no]');
      if (!nodes || !nodes.length) return;
      let max = 0;
      nodes.forEach((n) => {
        max = Math.max(max, n.getBoundingClientRect().width);
      });
      const next = Math.ceil(max) + 20;
      setJobAutoW((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };
    measureJob();
    if (document.fonts?.ready) document.fonts.ready.then(measureJob);
  }, [displayRows]);

  const isStale = debouncedSearchTerm !== searchTerm;

  const computeTotals = (rows) => {
    const sum = (f) => rows.reduce((acc, r) => acc + (Number(r[f]) || 0), 0);
    const totalPo = rows.reduce((acc, r) => acc + getPoNetOfVat(r), 0);
    const totalReceived = rows.reduce(
      (acc, r) => acc + (r.received || 0) + (r.seed_received || 0),
      0,
    );
    const totalBilled = rows.reduce(
      (acc, r) => acc + (r.billed_waiting || 0) + (r.seed_billed_waiting || 0),
      0,
    );
    const totalBilledLawsuit = rows.reduce(
      (acc, r) =>
        r.legal_lawsuit
          ? acc + (r.billed_waiting || 0) + (r.seed_billed_waiting || 0)
          : acc,
      0,
    );
    const totalBilledExLawsuit = totalBilled - totalBilledLawsuit;
    const col14 = rows.reduce(
      (acc, r) => acc + (getBgColumnValues(r).col14 || 0),
      0,
    );
    const col15 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r).col15,
      0,
    );
    const col16 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r).col16,
      0,
    );
    const col12 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r).col12,
      0,
    );
    const col17 = rows.reduce(
      (acc, r) => acc + getBgColumnValues(r).col17,
      0,
    );
    const col17Lawsuit = rows.reduce(
      (acc, r) =>
        r.legal_lawsuit
          ? acc + (getBgColumnValues(r).col17 || 0)
          : acc,
      0,
    );
    const profit = rows.reduce((acc, r) => acc + getEstimatedProfit(r), 0);
    return {
      po: totalPo,
      billed: totalBilledExLawsuit,
      billedLawsuit: totalBilledLawsuit,
      received: totalReceived,
      poRemain: totalPo - totalReceived - totalBilled,
      targetProfit: rows.reduce((acc, r) => acc + getTargetProfit(r), 0),
      budget: sum('total_budget'),
      // ยอดรวม BG Remain now (col8) เอาเฉพาะโครงการที่เหลือเป็นบวกมารวมกัน โครงการที่ติดลบ
      // (เกินงบ) ไม่เอามาหักลบยอดรวม — ตามที่ผู้ใช้ระบุ
      projectedFund: rows.reduce((acc, r) => {
        const v = getProjectedFund(r);
        return v > 0 ? acc + v : acc;
      }, 0),
      spent: rows.reduce((acc, r) => acc + getPaidValue(r), 0),
      col12,
      expected: rows.reduce((acc, r) => acc + getExpectedValue(r), 0),
      apPendingUnpaid: sum('ap_pending_unpaid'),
      col14,
      col15,
      refundPending: rows.reduce((acc, r) => acc + getRefundPendingValue(r), 0),
      col16,
      col17,
      col17Lawsuit,
      profit,
    };
  };

  // แยกแถวเป็น 3 กลุ่ม (ดู rowGroup): ปกติ / รับเงินหมดแล้วแต่ยังมีภาระผูกพัน (เฉพาะ toggle ปิด) / ฟ้องร้อง
  // แต่ละกลุ่มมียอดรวมของตัวเอง — ยอดรวมหัวตาราง (กรอบแดง) และการ์ดด้านบนใช้เฉพาะกลุ่มปกติ
  const groupTotals = useMemo(() => {
    const normalRows = [];
    const paidRows = [];
    const lawsuitRows = [];
    const judgedRows = [];
    displayRows.forEach((r) => {
      const g = rowGroup(r, includeCompletedCommitted);
      (g === 3
        ? judgedRows
        : g === 2
        ? lawsuitRows
        : g === 1
        ? paidRows
        : normalRows
      ).push(r);
    });
    return {
      normal: computeTotals(normalRows),
      paid: computeTotals(paidRows),
      lawsuit: computeTotals(lawsuitRows),
      judged: computeTotals(judgedRows),
    };
  }, [displayRows, includeCompletedCommitted]);
  const totals = groupTotals.normal;

  // การ์ดสรุปด้านบน (ProjectList) — ไม่นับกลุ่มฟ้องร้อง/รับเงินหมดแล้ว ยกเว้นยอดฟ้องร้องที่การ์ดเดิมแยกแสดงเอง
  // (billedLawsuit, col17Lawsuit) ให้ป้ายฟ้องร้องและสวิตช์ "รวมฟ้องร้อง" ของการ์ดยังทำงานเหมือนเดิม
  const cardTotals = useMemo(
    () => ({
      ...groupTotals.normal,
      // การ์ด "วางบิลแล้ว" รวมยอดวางบิลของกลุ่มปกติ + กลุ่มส่งงานครบแต่ยังมีภาระผูกพัน (ไม่รวมฟ้องร้อง/พิพากษาแล้ว)
      billed: groupTotals.normal.billed + groupTotals.paid.billed,
      // สรุปอนาคตต้องจ่ายอีกรวม ฐาน = กลุ่มปกติ + กลุ่มส่งงานครบแต่ยังมีภาระผูกพัน (ฟ้องร้อง/พิพากษาแล้วบวกเพิ่มที่ ProjectList
      // ตามสวิตช์ "รวมยอดค้างจ่ายในงานที่ฟ้องร้อง" โดยใช้ col17Lawsuit / col17Judged)
      col17: groupTotals.normal.col17 + groupTotals.paid.col17,
      col17Lawsuit: groupTotals.lawsuit.col17,
      billedLawsuit: groupTotals.lawsuit.billedLawsuit,
      // ป้าย "พิพากษาแล้ว-รอจ่าย": ยอดวางบิลทั้งหมดของกลุ่มพิพากษาแล้ว (billed ไม่รวมแถวฟ้องร้อง จึงต้องบวก billedLawsuit กลับ)
      billedJudged: groupTotals.judged.billed + groupTotals.judged.billedLawsuit,
      col17Judged: groupTotals.judged.col17,
    }),
    [groupTotals],
  );

  // แถวยอดรวมของกลุ่ม (ฟ้องร้อง / รับเงินหมดแล้วแต่ยังไม่ปิด) — ตัวเลขล้วนๆ ตามลำดับคอลัมน์เดียวกับหัวตาราง
  const renderGroupTotalsRow = (t, bg, label, labelColor) => {
    const money = {
      6: t.po,
      7: t.billed + t.billedLawsuit,
      8: t.received,
      9: t.poRemain,
      18: t.targetProfit,
      33: t.budget,
      11: t.spent,
      10: t.projectedFund,
      35: t.expected,
      32: t.apPendingUnpaid,
      16: t.col16,
      12: t.col12,
      34: t.refundPending,
      17: t.col17,
      15: t.col15,
      14: t.col14 < 0 ? t.col14 : null,
      19: t.profit,
    };
    const colorOf = {
      9: t.poRemain < 0 ? '#b91c1c' : undefined,
      17: t.col17 > 0 ? '#b91c1c' : undefined,
      15: '#16a34a',
      14: '#b91c1c',
      19: t.profit < 0 ? '#b91c1c' : undefined,
      34: '#059669',
    };
    // คอลัมน์หน้า PO (#, เลขงาน, สถานะ, ตรวจสอบ, %, ผู้ว่าจ้าง, โครงการ) รวมเป็นเซลล์เดียวใส่ชื่อกลุ่ม
    const labelSpan = 6 + (showCol2 ? 1 : 0);
    const cols = [
      ...(showGroup1 ? [37, 38] : []),
      6,
      ...(showGroup1 ? [7, 8] : [7]),
      9,
      ...(showGroup1 ? [18, 33] : []),
      11, 10,
      ...(showGroup1 ? [35, 32, 16, 12, 34] : []),
      17,
      ...(showGroup1 ? [27, 28, 31, 29, 30] : []),
      15, 14, 19, 13, 20, 26,
      ...(onEditProject || onDeleteProject ? [39] : []),
    ];
    return (
      <TableRow>
        {/* คอลัมน์หน้า PO แบ่งเป็น 3 ก้อนตามการตรึงของแถวข้อมูล: (#, เลขงาน) ตรึง / (สถานะ, ตรวจสอบ, %) ไม่ตรึง /
            (ผู้ว่าจ้าง, โครงการ) ตรึง — ใส่ชื่อกลุ่มในก้อนหลัง เพื่อให้ข้อความอยู่ในส่วนที่ตรึงและไม่บังคอลัมน์อื่น */}
        <TableCell
          colSpan={2}
          sx={{ ...TD_SX, bgcolor: bg, position: 'sticky', left: 0, zIndex: 2 }}
        />
        <TableCell colSpan={labelSpan - 4} sx={{ ...TD_SX, bgcolor: bg }} />
        <TableCell
          colSpan={2}
          sx={{
            ...TD_SX,
            bgcolor: bg,
            textAlign: 'center',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: labelColor,
            whiteSpace: 'nowrap',
            ...(stickyLeftOf[5] !== undefined && {
              position: 'sticky',
              left: stickyLeftOf[5],
              zIndex: 2,
            }),
          }}
        >
          {label}
        </TableCell>
        {cols.map((c) => (
          <TableCell
            key={c}
            sx={{
              ...TD_SX, // padding/เส้นขอบเดียวกับแถวข้อมูล ให้ตัวเลขชิดขวาตรงกัน
              bgcolor: bg,
              textAlign: 'right',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: colorOf[c] || '#082f49',
              whiteSpace: 'nowrap',
              ...(stickyLeftOf[c] !== undefined && {
                position: 'sticky',
                left: stickyLeftOf[c],
                zIndex: 2,
              }),
            }}
          >
            {money[c] != null ? fmt(money[c]) : ''}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  useEffect(() => {
    onTotalsChange?.(cardTotals);
  }, [cardTotals]);

  // ส่งลิสต์ id ตามลำดับที่กรอง/เรียงอยู่ตอนนี้ขึ้นไปให้ parent — ใช้ทำปุ่มเลื่อน
  // ถัดไป/ก่อนหน้าใน popup P111 (ทั้ง modal และหน้าต่างแยก)
  useEffect(() => {
    onVisibleIdsChange?.(displayRows.map((r) => r._id));
  }, [displayRows]);

  // ── Export Excel — คอลัมน์เดียวกับตารางที่เห็น (ใช้ displayRows หลังกรอง/sort) ────
  const handleExportExcel = () => {
    const toISODate = (d) => {
      if (!d) return '';
      try {
        return new Date(d).toISOString().split('T')[0];
      } catch {
        return '';
      }
    };

    const excelRows = displayRows.map((row) => {
      const mergedRow = row;
      const po = getPoNetOfVat(row);
      const received = row.received ?? 0;
      const billedWaiting = row.billed_waiting ?? 0;
      const seedReceived = getVal(row, 'seed_received') ?? 0;
      const seedBilled = getVal(row, 'seed_billed_waiting') ?? 0;
      const billedCombined = billedWaiting + seedBilled;
      const receivedCombined = received + seedReceived;
      const poRemaining = po - receivedCombined - billedCombined;

      const deliveryDate = getVal(row, 'delivery_date');
      const creditDays = row.client_id?.credit_days;
      const paymentCycleDay = getVal(row, 'payment_cycle_day');
      let expectedDate = null;
      if (deliveryDate && creditDays != null && creditDays !== '') {
        const base = new Date(deliveryDate);
        base.setDate(base.getDate() + Number(creditDays));
        if (paymentCycleDay != null && paymentCycleDay !== '') {
          const day = Number(paymentCycleDay);
          const sameMonth = new Date(
            base.getFullYear(),
            base.getMonth(),
            day,
          );
          expectedDate =
            sameMonth >= base
              ? sameMonth
              : new Date(base.getFullYear(), base.getMonth() + 1, day);
        } else {
          expectedDate = base;
        }
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysLeft = expectedDate
        ? Math.round(
            (expectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
          )
        : '';

      const { col14, col15, col16, col17, col12 } = getBgColumnValues(mergedRow);
      const realprofit = getEstimatedProfit(mergedRow);
      const realPct = po > 0 ? (realprofit / po) * 100 : 0;
      const projectedFund = getProjectedFund(mergedRow);

      const ws = getVal(row, 'work_status') || '';
      const fs = getVal(row, 'finance_status') || '';
      const bgUsageCategory = getVal(row, 'bg_usage_category') || '';
      const workOpt = WORK_OPTIONS.find((o) => o.key === ws);
      const finOpt = FINANCE_OPTIONS.find((o) => o.key === fs);
      const bgUsageOpt = BG_USAGE_OPTIONS.find(
        (o) => o.key === bgUsageCategory,
      );

      return {
        '1 เลขงาน': row.project_number || '',
        '2 สถานะ: งาน': workOpt?.label || '',
        '2 สถานะ: การเงิน': finOpt?.label || '',
        '2 สถานะ: การใช้ BG': bgUsageOpt?.label || '',
        '2 Warranty': getVal(row, 'legal_warranty') ? 'มี' : '',
        '2 เจ้าหนี้ค้างจ่าย': getVal(row, 'legal_debt') ? 'มี' : '',
        '2 ฟ้องร้อง': getVal(row, 'legal_lawsuit') ? 'มี' : '',
        '3 ความคืบหน้า %': getVal(row, 'progress') ?? 0,
        '4 โครงการ': row.name || '',
        '5 ผู้ว่าจ้าง': getCustomerName(row) || '',
        '6 PO': po,
        '7 วางบิลแล้วรอรับเงิน': billedCombined,
        '8 รับเงินมาแล้ว': receivedCombined,
        '9 ยอดค้างส่งงานจากPO': poRemaining,
        '27 วันที่ส่งงาน': toISODate(deliveryDate),
        '28 เครดิต(วัน)': creditDays ?? '',
        '31 รอบจ่ายเงิน': paymentCycleDay ?? '',
        '29 วันที่คาดรับเงิน': toISODate(expectedDate),
        '30 รับเงินอีก(วัน)': daysLeft,
        '33 BG ที่ตั้งไว้': row.total_budget || 0,
        '11 BG PAID': getPaidValue(row),
        '10 BG Remain now': projectedFund,
        '35 รายการตั้งเบิกที่รอจ่าย': getExpectedValue(row),
        '32 AP PENDING ตามงบย่อย': row.ap_pending_unpaid || 0,
        '16 ต้องใช้อีก': col16,
        '12 BGเหลือที่มีสิทธิจ่าย': col12,
        '34 รอเงินคืน(เช่นค่ามัดจำ)': getRefundPendingValue(row),
        '17 สรุปอนาคตต้องจ่ายอีกรวม': col17,
        '15 สรุป BG คงเหลือ': col15,
        '14 สรุป BG เกิน': col14 ?? 0,
        '19 สรุปกำไรคาดการณ์': realprofit,
        '13 %ใช้ BG': row.bg_used_pct ?? 0,
        '20 %กำไร': realPct,
        '26 หมายเหตุ': getVal(row, 'dashboard_note') || '',
      };
    });

    const headerKeys = Object.keys(excelRows[0] || {});
    const colIndex = (key) => headerKeys.indexOf(key);

    // ── แถวสรุปตัวกรองที่มีผลตอน export (เฉพาะตัวที่กำลังกรองอยู่จริง) ──────────
    const filterParts = [];
    typeFilter.forEach((p) => filterParts.push(`ประเภท: ${p}`));
    if (testFilter) filterParts.push('ประเภท: Test');
    if (statusFilter) {
      const meta = FILTER_META[statusFilter] || STATUS_MAP[statusFilter] || {};
      filterParts.push(`สถานะ: ${meta.label || statusFilter}`);
    }
    if (customerFilter) filterParts.push(`ผู้ว่าจ้าง: ${customerFilter}`);
    if (projectFilter) filterParts.push(`โครงการ: ${projectFilter}`);
    if (searchTerm) filterParts.push(`ค้นหา: "${searchTerm}"`);
    if (excludeFilter.size > 0) {
      const names = [...excludeFilter].map((k) => FILTER_META[k]?.label || k);
      filterParts.push(`ไม่รวม: ${names.join(', ')}`);
    }
    const filterSummary = filterParts.length
      ? `ตัวกรองที่ใช้: ${filterParts.join(' | ')}`
      : 'ตัวกรองที่ใช้: ไม่มี (แสดงข้อมูลทั้งหมด)';

    // row0 = สรุปตัวกรอง, row1 = เว้นว่าง, row2 = หัวตาราง, row3.. = ข้อมูล
    const worksheet = XLSX.utils.aoa_to_sheet([[filterSummary], []]);
    const dataStartRow = 3;
    XLSX.utils.sheet_add_json(worksheet, excelRows, {
      origin: { r: dataStartRow - 1, c: 0 },
      skipHeader: false,
    });

    // ── แถวรวมยอด (แนวนอน ต่อท้ายข้อมูล) — สูตร/ค่าเดียวกับแถว Total บนหน้าเว็บ ──
    // แถวข้อมูลอยู่ที่ index dataStartRow..dataStartRow+length-1 แถวรวมต้องอยู่ "ถัดจาก"
    // แถวข้อมูลแถวสุดท้าย (ห้ามใช้ dataStartRow-1+length เพราะนั่นคือ index แถวข้อมูลแถวสุดท้ายเอง
    // จะเขียนทับข้อมูลแถวสุดท้ายหาย)
    const totalsRowIdx = dataStartRow + excelRows.length;
    const totalsRowArr = new Array(headerKeys.length).fill('');
    totalsRowArr[colIndex('1 เลขงาน')] = 'รวม';
    totalsRowArr[colIndex('6 PO')] = totals.po;
    totalsRowArr[colIndex('7 วางบิลแล้วรอรับเงิน')] = totals.billed;
    totalsRowArr[colIndex('8 รับเงินมาแล้ว')] = totals.received;
    totalsRowArr[colIndex('9 ยอดค้างส่งงานจากPO')] = totals.poRemain;
    totalsRowArr[colIndex('33 BG ที่ตั้งไว้')] = totals.budget;
    totalsRowArr[colIndex('11 BG PAID')] = totals.spent;
    totalsRowArr[colIndex('10 BG Remain now')] = totals.projectedFund;
    totalsRowArr[colIndex('35 รายการตั้งเบิกที่รอจ่าย')] = totals.expected;
    totalsRowArr[colIndex('32 AP PENDING ตามงบย่อย')] = totals.apPendingUnpaid;
    totalsRowArr[colIndex('16 ต้องใช้อีก')] = totals.col16;
    totalsRowArr[colIndex('12 BGเหลือที่มีสิทธิจ่าย')] = totals.col12;
    totalsRowArr[colIndex('34 รอเงินคืน(เช่นค่ามัดจำ)')] = totals.refundPending;
    totalsRowArr[colIndex('17 สรุปอนาคตต้องจ่ายอีกรวม')] = totals.col17;
    totalsRowArr[colIndex('15 สรุป BG คงเหลือ')] = totals.col15;
    totalsRowArr[colIndex('14 สรุป BG เกิน')] = totals.col14;
    totalsRowArr[colIndex('19 สรุปกำไรคาดการณ์')] = totals.profit;
    XLSX.utils.sheet_add_aoa(worksheet, [totalsRowArr], {
      origin: { r: totalsRowIdx, c: 0 },
    });

    // ── สรุปรวมแนวตั้ง (label/value ทีละคอลัมน์) — ดูง่ายไม่ต้องเลื่อนขวา ─────────
    const vertPairs = [
      ['6 PO', totals.po],
      ['7 วางบิลแล้วรอรับเงิน', totals.billed],
      ['8 รับเงินมาแล้ว', totals.received],
      ['9 ยอดค้างส่งงานจากPO', totals.poRemain],
      ['33 BG ที่ตั้งไว้', totals.budget],
      ['11 BG PAID', totals.spent],
      ['10 BG Remain now', totals.projectedFund],
      ['35 รายการตั้งเบิกที่รอจ่าย', totals.expected],
      ['32 AP PENDING ตามงบย่อย', totals.apPendingUnpaid],
      ['16 ต้องใช้อีก', totals.col16],
      ['12 BGเหลือที่มีสิทธิจ่าย', totals.col12],
      ['34 รอเงินคืน(เช่นค่ามัดจำ)', totals.refundPending],
      ['17 สรุปอนาคตต้องจ่ายอีกรวม', totals.col17],
      ['15 สรุป BG คงเหลือ', totals.col15],
      ['14 สรุป BG เกิน', totals.col14],
      ['19 สรุปกำไรคาดการณ์', totals.profit],
    ];
    // เริ่มที่คอลัมน์ B (index 1) เพราะคอลัมน์ A แคบเกินไปสำหรับข้อความ label
    XLSX.utils.sheet_add_aoa(worksheet, [['SUM'], ...vertPairs], {
      origin: { r: totalsRowIdx + 2, c: 1 },
    });

    // ── Accounting format กับตัวเลขทุกช่อง ยกเว้นคอลัมน์เปอร์เซ็นต์ ──────────────
    const PERCENT_KEYS = new Set([
      '3 ความคืบหน้า %',
      '13 %ใช้ BG',
      '20 %กำไร',
    ]);
    const ACCOUNTING_FMT = '_-* #,##0.00_-;-* #,##0.00_-;_-* "-"??_-;_-@_-';
    const fullRange = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = fullRange.s.r; R <= fullRange.e.r; R++) {
      for (let C = fullRange.s.c; C <= fullRange.e.c; C++) {
        if (PERCENT_KEYS.has(headerKeys[C])) continue;
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[addr];
        if (cell && cell.t === 'n') cell.z = ACCOUNTING_FMT;
      }
    }

    // ── merge แถวสรุปตัวกรอง, ความสูงแถวหัวตาราง, ความกว้างคอลัมน์ ──────────────
    // หมายเหตุ: library 'xlsx' (community edition) ที่ใช้อยู่ไม่รองรับการเขียนสี
    // พื้นหลัง/ฟอนต์ของเซลล์ (ทดสอบแล้วค่า cell.s ถูกทิ้งตอน write) เลยใส่ได้แค่
    // ความกว้าง/ความสูง/merge เท่านั้น — ถ้าต้องการสีพื้นหัวตารางด้วยต้องเปลี่ยนไปใช้
    // library 'exceljs' แทน
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headerKeys.length - 1 } },
    ];
    worksheet['!rows'] = [];
    worksheet['!rows'][dataStartRow - 1] = { hpx: 40 };
    worksheet['!cols'] = REFERENCE_COL_WIDTHS.map((wch) => ({ wch }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dashboard');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `project_dashboard_${toISODate(new Date())}.xlsx`);
  };

  useImperativeHandle(ref, () => ({
    exportExcel: handleExportExcel,
    resetSort: () => setSortCriteria(DEFAULT_SORT_CRITERIA),
    removeZeroExclude: (key) => toggleZeroExclude(key, 0),
    removeExcludedProject: (pn) => toggleExcludedProject(pn),
    removeExcludedCustomer: (cn) => toggleExcludedCustomer(cn),
  }));

  const TH_TOT = {
    position: 'absolute',
    bottom: 3,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#082f49',
    lineHeight: 1,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    pointerEvents: 'none', // กันบังพื้นที่ลาก resize ที่ขอบขวา (ResizeHandle ทับซ้อนกับแถบนี้ตรงมุมล่างขวา)
  };

  // ขนาดอ้างอิงของแถบตัวกรอง — ยึดตาม chip "เจ้าหนี้ค้างจ่าย" (height 22, font 0.79rem)
  // ให้ทุกปุ่ม/ป้ายในแถบนี้มีขนาดเท่ากันหมด
  const FILTERBAR_BTN_SX = {
    height: 16.5,
    minHeight: 16.5,
    fontSize: '0.59rem',
    fontWeight: 600,
    lineHeight: 1,
    py: 0,
    px: 1,
    whiteSpace: 'nowrap',
    borderRadius: '6px',
  };

  // เทียบ "เป็นค่า default หรือไม่" ด้วยค่าจริง (key+dir ทีละตัว) ไม่ใช่ === reference เฉยๆ —
  // เพราะกด header คลิกเองจนได้ผลลัพธ์ตรงกับ default เป๊ะ (เช่น progress asc + customer asc)
  // ก็ยังได้ array คนละ reference กับ DEFAULT_SORT_CRITERIA (cycleSortByKey สร้าง array ใหม่เสมอ
  // ไม่เคย fallback กลับไปใช้ reference เดิม) ถ้าเทียบด้วย === เฉยๆ ป้ายจะค้างขึ้นทั้งที่ sort
  // ตรงกับ default แล้วจริงๆ — บั๊กนี้เจอจากการกด header จนตรงกับ default พอดี
  const isDefaultSort =
    sortCriteria === DEFAULT_SORT_CRITERIA ||
    (sortCriteria.length === DEFAULT_SORT_CRITERIA.length &&
      sortCriteria.every(
        (s, i) =>
          s.key === DEFAULT_SORT_CRITERIA[i].key &&
          s.dir === DEFAULT_SORT_CRITERIA[i].dir,
      ));

  // แจ้ง parent (ProjectList.js) ว่า sort ตรงกับ default อยู่หรือไม่ — ป้าย "เรียงลำดับกำหนดเอง"
  // ย้ายไปแสดงที่แถบบนสุดแล้ว (รวมกับป้าย filter อื่นๆ ที่ user สั่งเอง) แทนที่จะอยู่ในแถบตัวกรองนี้
  useEffect(() => {
    onSortDefaultChange?.(isDefaultSort);
  }, [isDefaultSort]);

  // เก็บคำสั่ง sort ไว้ที่ ProjectList เพื่อไม่ให้หายเมื่อสลับ T-12/T-13 แล้วตารางถูก remount
  useEffect(() => {
    onSortCriteriaChange?.(sortCriteria);
  }, [sortCriteria, onSortCriteriaChange]);

  // แจ้ง parent เช่นกันตอน excludedZeroFields เปลี่ยน — ป้าย "ไม่รวม 0/ว่าง: X" ย้ายไปแสดงที่แถบบนสุด
  // เหมือน sort ด้านบน (state ยังอยู่ที่นี่เหมือนเดิม เพราะใช้ในตรรกะกรองแถว/ไฮไลต์ header ภายในลึกมาก
  // parent แค่โชว์ผลลัพธ์ + สั่งลบทีละอันผ่าน imperative handle ด้านล่าง ไม่ได้ยก state ขึ้นไปทั้งชุด)
  useEffect(() => {
    onExcludedZeroFieldsChange?.(excludedZeroFields);
  }, [excludedZeroFields]);

  // เช่นเดียวกัน — ป้าย "ไม่รวมโครงการ/ผู้ว่าจ้าง: X" ย้ายไปแสดงแถวบนสุดแล้ว
  useEffect(() => {
    onExcludedProjectsChange?.(excludedProjects);
  }, [excludedProjects]);
  useEffect(() => {
    onExcludedCustomersChange?.(excludedCustomers);
  }, [excludedCustomers]);

  return (
    <Box>
      {/* Filter bar */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          position: 'relative',
          mb: 1.5,
          bgcolor: '#f0f9ff',
          border: '1px solid rgba(14,116,144,0.12)',
          borderRadius: '10px',
          px: 1.5,
          py: 0.9,
          gap: 0.75,
        }}
      >
        {/* ป้ายแจ้งเตือน "sort เบี่ยงจาก default" (↺ เรียงลำดับกำหนดเอง) ย้ายไปแถบบนสุด
            (ProjectList.js ต่อท้ายช่องค้นหา) แล้ว เพราะเป็นป้ายเตือนสถานะผิดปกติ ไม่ใช่ชุดควบคุม
            filter หลัก — ป้าย Default/JS_/status/exclude chips อื่นๆ ยังอยู่ที่นี่เหมือนเดิม
            (ซ้าย/กลาง/ขวา แบ่ง 3 โซนเท่ากันให้โซนกลางจัดกึ่งกลางแถบจริงๆ) */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            size="small"
            variant={filterOpen ? 'contained' : 'outlined'}
            startIcon={<TuneIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => setFilterOpen(true)}
            sx={{
              ...FILTERBAR_BTN_SX,
              height: 22,
              minHeight: 22,
              fontSize: '0.79rem',
            }}
          >
            {L('ตัวกรอง', 'Filter')}
          </Button>
          {isLoading && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CircularProgress size={12} />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.59rem' }}
              >
                {L(
                  `กำลังโหลด ${projects?.rows?.length || 0} รายการ...`,
                  `Loading ${projects?.rows?.length || 0} items...`,
                )}
              </Typography>
            </Stack>
          )}
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          sx={{ flex: 1, gap: 0.75, rowGap: 0.5 }}
        >
        {(() => {
          // เดิมเช็คแค่ statusFilter/jsFilter/customerFilter/excludeFilter — ไม่ครอบคลุมตัวกรอง
          // manual อื่นที่กดผ่าน column อื่น (project/customer/เดือน/ปี/exclude-zero/showGroup1)
          // ทำให้ปุ่มค้างสีเทา (isDefault=true) แม้กำลังกรองอยู่จริง — เพิ่มเงื่อนไขให้ตรงกับ
          // hasActiveFilter ที่ใช้กับปุ่ม ESC เคลียร์ filter (ดูจุดเดียวกันด้านบน)
          const isDefault =
            statusFilter === 'has_commitment' &&
            JSON.stringify(typeFilter) === JSON.stringify(defaultTypeFilter) &&
            !testFilter &&
            !customerFilter &&
            excludeFilter.size === defaultExcludeFilter.size &&
            [...defaultExcludeFilter].every((k) => excludeFilter.has(k)) &&
            excludedProjects.size === 0 &&
            excludedCustomers.size === 0 &&
            !projectFilter &&
            monthFilter === '' &&
            yearFilter === '' &&
            excludedMonths.size === 0 &&
            excludedYears.size === 0 &&
            excludedZeroFields.size === 0 &&
            showGroup1 === false;
          return (
            <Chip
              label={L('↺ ค่าเริ่มต้น', '↺ Default')}
              onClick={() => {
                setStatusFilter('has_commitment');
                setTypeFilter(defaultTypeFilter);
                setTestFilter(false);
                onExcludeFilterChange?.(new Set(defaultExcludeFilter));
                setCustomerFilter('');
                setExcludedProjects(new Set());
                setExcludedCustomers(new Set());
                setProjectFilter('');
                setMonthFilter('');
                setYearFilter('');
                setExcludedMonths(new Set());
                setExcludedYears(new Set());
                setExcludedZeroFields(new Set());
                setShowGroup1(false);
              }}
              size="small"
              sx={{
                height: 16.5,
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: '6px',
                '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                ...(isDefault
                  ? {
                      bgcolor: 'transparent',
                      color: 'text.disabled',
                      border: '1px solid #e2e8f0',
                    }
                  : {
                      bgcolor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #fcd34d',
                      '&:hover': { bgcolor: '#fde68a' },
                    }),
              }}
            />
          );
        })()}
        {typeFilter.map((p) => (
          <Chip
            key={p}
            label={L(`ประเภท: ${p}`, `Type: ${p}`)}
            onDelete={() => setTypeFilter((v) => v.filter((x) => x !== p))}
            size="small"
            sx={{
              height: 16.5,
              fontWeight: 600,
              borderRadius: '6px',
              bgcolor: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fcd34d',
              '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
              '& .MuiChip-deleteIcon': {
                fontSize: 10.5,
                mr: 0.25,
                color: '#92400e',
              },
            }}
          />
        ))}
        {testFilter && (
          <Chip
            label="ประเภท: Test"
            onDelete={() => setTestFilter(false)}
            size="small"
            sx={{
              height: 16.5,
              fontWeight: 600,
              borderRadius: '6px',
              bgcolor: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fcd34d',
              '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
              '& .MuiChip-deleteIcon': { fontSize: 13 },
            }}
          />
        )}
        {/* ป้าย "ไม่รวมโครงการ/ผู้ว่าจ้าง/0-ว่าง: X" ย้ายไปแสดงแถวบนสุด (ProjectList.js) แล้วทั้งหมด
            — เป็น filter ที่ user คลิกขวาเพิ่มเองทีหลัง ไม่ใช่ค่าที่กรองอยู่ตั้งแต่เข้าเพจ (state ยังอยู่
            ที่นี่เหมือนเดิม ดู onExcludedProjectsChange, onExcludedCustomersChange,
            onExcludedZeroFieldsChange effects และ removeExcludedProject, removeExcludedCustomer,
            removeZeroExclude ใน imperative handle ด้านบน) */}
        {/* เฉพาะตอน statusFilter เป็นค่า default ('has_commitment') เท่านั้น — ถ้า user คลิก
            การ์ดสรุป/badge อื่นเปลี่ยน statusFilter ไปเป็นอย่างอื่น (เช่น col7_positive จากคลิก
            "Invoiced [VAT incl]") ป้ายจะไปโผล่แถวบนแทน (ดู ProjectList.js) เพราะถือเป็น filter
            ที่ user สั่งเพิ่มทีหลัง ไม่ใช่ค่าที่กรองอยู่ตั้งแต่เข้าเพจ */}
        {/* โชว์ตราบใดที่ statusFilter ปัจจุบันยังมีเงื่อนไข "มีภาระผูกพัน" ผูกอยู่ในตัว (has_commitment
            เอง, หรือ col7_positive/col9_positive/col9_or_col7_nonzero ที่เช็ค hasCom ซ้อนอยู่ข้างใน
            ตรรกะกรองอยู่แล้ว) — การกด badge เปลี่ยนไปดูมุมอื่นไม่ได้ทำให้เงื่อนไขเดิมหายไป ตามที่ user ยืนยัน */}
        {[
          'has_commitment',
          'col7_positive',
          'col9_positive',
          'col9_or_col7_nonzero',
        ].includes(statusFilter) &&
          (() => {
            const meta =
              FILTER_META.has_commitment || STATUS_MAP.has_commitment || {};
            const label = L(
              meta.label || 'มีภาระผูกพัน',
              meta.labelEn || 'Has Commitment',
            );
            return (
              <Chip
                label={`${label} · ${displayRows.length}`}
                onDelete={() => setStatusFilter('')}
                size="small"
                sx={{
                  height: 16.5,
                  fontWeight: 600,
                  borderRadius: '6px',
                  bgcolor: '#fef3c7',
                  color: '#92400e',
                  border: '1px solid #fcd34d',
                  '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                  '& .MuiChip-deleteIcon': {
                    fontSize: 10.5,
                    mr: 0.25,
                    color: '#92400e',
                  },
                }}
              />
            );
          })()}
        {[...excludeFilter].map((key) => {
          const meta = FILTER_META[key] || {};
          const label = L(meta.label || key, meta.labelEn || meta.label || key);
          return (
            <Chip
              key={key}
              label={`− ${label}${
                excludeCounts[key] != null ? ` · ${excludeCounts[key]}` : ''
              }`}
              onDelete={() =>
                onExcludeFilterChange?.((prev) => {
                  const s = new Set(prev);
                  s.delete(key);
                  return s;
                })
              }
              size="small"
              sx={{
                height: 16.5,
                fontWeight: 600,
                borderRadius: '6px',
                bgcolor: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                '& .MuiChip-label': { px: 0.8, fontSize: '0.59rem' },
                '& .MuiChip-deleteIcon': {
                  fontSize: 10.5,
                  mr: 0.25,
                  color: '#b91c1c',
                  '&:hover': { color: '#991b1b' },
                },
              }}
            />
          );
        })}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          sx={{ flex: 1, gap: 0.75 }}
        >
        <Tooltip title="ยอดรวมหัวตาราง (กรอบแดง): เปิด = นับรวมโครงการที่ส่งงานครบแล้ว (ยอดค้างส่งงานจาก PO = 0), ปิด = ไม่นับ (ค่าเริ่มต้น)">
          <FormControlLabel
            sx={{ mx: 'auto' }}
            control={
              <Switch
                size="small"
                color="warning"
                checked={includeCompletedCommitted}
                onChange={(e) => setIncludeCompletedCommitted(e.target.checked)}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: includeCompletedCommitted ? '#ea580c' : 'text.secondary',
                }}
              >
                {includeCompletedCommitted
                  ? L(
                      'รวมโครงการที่ส่งงานครบแล้ว และยังมีภาระผูกพัน',
                      'Incl. fully paid projects, not yet closed',
                    )
                  : L(
                      'ไม่รวมโครงการที่ส่งงานครบแล้ว และยังมีภาระผูกพัน',
                      'Excl. fully paid projects, not yet closed',
                    )}
              </Typography>
            }
          />
        </Tooltip>
        <Tooltip title="To show column number or formula">
          <Button
            variant={showColNums ? 'contained' : 'outlined'}
            color="secondary"
            size="small"
            onClick={() => setShowColNums((v) => !v)}
            sx={FILTERBAR_BTN_SX}
          >
            {L('Col No_', 'Col No_')}
          </Button>
        </Tooltip>
        <Tooltip title="To show all column">
          <Badge
            variant="dot"
            color="error"
            invisible={showGroup1}
            overlap="rectangular"
            sx={{ '& .MuiBadge-badge': { top: 2, right: 2 } }}
          >
            <Button
              variant={showGroup1 ? 'outlined' : 'contained'}
              color={showGroup1 ? 'inherit' : 'error'}
              size="small"
              onClick={() => setShowGroup1((v) => !v)}
              sx={FILTERBAR_BTN_SX}
            >
              {showGroup1 ? L('− ซ่อน Col', '− Hide Col') : L('+ แสดง Col', '+ Show Col')}
            </Button>
          </Badge>
        </Tooltip>
        {/* ปุ่ม help (?) ปิดไว้ก่อนชั่วคราว — เป็นงานค้าง ยังไม่ได้แก้ไข ยัง import HelpDialog +
            เก็บ helpOpen state ไว้เหมือนเดิม แค่ไม่ render ปุ่มเปิดให้กดเท่านั้น */}
        </Stack>
      </Stack>

      <TableContainer
        ref={containerRef}
        onMouseDown={onContainerMouseDown}
        sx={{
          maxHeight: isMobile ? 'calc(100dvh - 190px)' : '693px',
          overflowX: 'auto',
          overflowY: 'auto',
          // มือถือ: แตะได้ทันทีไม่รอตัดสินใจซูมสองครั้ง (ไม่งั้นแตะสองครั้งเปิดโครงการไม่ติด)
          touchAction: isMobile ? 'manipulation' : undefined,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: isDragging ? 'none' : 'auto',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)',
        }}
      >
        <Table
          size="small"
          sx={{
            // table-layout: auto — browser คำนวณความกว้างแต่ละคอลัมน์เองจากเนื้อหาจริง (เหมือน Excel
            // auto-fit) รับประกันไม่ล้นแน่นอน ค่า width ใน colgroup/cell ที่ตั้งไว้ (getW) กลายเป็นแค่
            // "ความกว้างขั้นต่ำที่แนะนำ" ไม่ใช่ค่าตายตัวอีกต่อไป (ต่างจาก table-layout:fixed เดิมที่ต้อง
            // คำนวณความกว้างทุกจุดให้ตรงกันเป๊ะ พลาดจุดเดียวก็บีบทับทั้งตาราง — debug ยากมาก)
            tableLayout: 'auto',
            minWidth: tableMinWidth,
            borderCollapse: 'separate',
            borderSpacing: 0,
          }}
        >
          <colgroup>
            <col style={{ width: 38 }} />
            {(showCol2 ? [1, 2, 36, 3, 5, 4] : [1, 36, 3, 5, 4]).map((i) => (
              <col key={i} style={{ width: getW(i) }} />
            ))}
            {(showGroup1 ? [7, 8] : [7]).map((i) => (
              <col key={i} style={{ width: getW(i) }} />
            ))}
            <col style={{ width: getW(6) }} />
            <col style={{ width: getW(9) }} />
            {showGroup1 &&
              [18, 33].map((i) => <col key={i} style={{ width: getW(i) }} />)}
            {[11, 10].map((i) => (
              <col key={i} style={{ width: getW(i) }} />
            ))}
            {showGroup1 &&
              [35, 32, 16, 12, 34].map((i) => <col key={i} style={{ width: getW(i) }} />)}
            <col style={{ width: getW(17) }} />
            {showGroup1 &&
              [27, 28, 31, 29, 30].map((i) => (
                <col key={i} style={{ width: getW(i) }} />
              ))}
            {[15, 14, 19, 13, 20].map((i) => (
              <col key={i} style={{ width: getW(i) }} />
            ))}
            <col style={{ width: getW(26) }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...TH_SX,
                  width: 38,
                  minWidth: 38,
                  maxWidth: 38,
                  position: 'sticky',
                  left: stickyLeftOf[0],
                  zIndex: 6,
                  textAlign: 'center',
                  fontSize: '0.76rem',
                  lineHeight: 1.2,
                }}
              >
                #<br />
                <span style={{ fontSize: '0.71rem', opacity: 0.7 }}>
                  {displayRows.length}
                </span>
              </TableCell>
              <TableCell
                sx={{
                  ...TH_SX,
                  width: getW(1),
                  minWidth: getW(1),
                  maxWidth: getW(1),
                  cursor: 'pointer',
                  userSelect: 'none',
                  ...(getSortDir('project_number') !== null && {
                    bgcolor: '#c09614',
                    color: '#fff',
                    borderBottom: '3px solid #d4a017',
                  }),
                  '&:hover': {
                    bgcolor:
                      getSortDir('project_number') !== null
                        ? '#9b780f'
                        : '#7dd3fc',
                  },
                  ...frzTh(1),
                }}
                onClick={(e) => handleHeaderSortClick('project_number', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('เลขงาน', 'Job No.')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {getSortDir('project_number') === 'asc' ? (
                      <ArrowDropUpIcon
                        sx={{
                          fontSize: 40,
                          color: '#ef4444',
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      />
                    ) : getSortDir('project_number') === 'desc' ? (
                      <ArrowDropDownIcon
                        sx={{
                          fontSize: 40,
                          color: '#ef4444',
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>
                <ResizeHandle onStart={(e) => startResize(1, e)} />
              </TableCell>
              {showCol2 && (
                <TableCell sx={{ ...TH_SX, width: getW(2), ...frzTh(2) }}>
                  <Stack
                    direction="row"
                    spacing={0.4}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <span>{L('สถานะ', 'Status')}</span>
                  </Stack>
                  <ResizeHandle onStart={(e) => startResize(2, e)} />
                </TableCell>
              )}
              <TableCell
                sx={{
                  ...TH_SX,
                  width: getW(36),
                  minWidth: getW(36),
                  textAlign: 'center',
                  ...frzTh(36),
                }}
              >
                {L('ตรวจสอบ', 'Review')}
                <ResizeHandle onStart={(e) => startResize(36, e)} />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('progress', TH_SX),
                  width: getW(3),
                  minWidth: getW(3),
                  maxWidth: getW(3),
                  ...frzTh(3),
                }}
                onClick={(e) => handleHeaderSortClick('progress', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>%</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('progress')}</Box>
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(3, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('customer', TH_SX),
                  width: getW(5),
                  minWidth: getW(5),
                  maxWidth: getW(5),
                  ...frzTh(5),
                  ...((customerFilter || excludedCustomers.size > 0) && {
                    bgcolor: '#7c3aed',
                    color: '#fff',
                    '&:hover': { bgcolor: '#6d28d9' },
                  }),
                }}
                onClick={(e) => {
                  if (customerFilter || excludedCustomers.size > 0) {
                    setCustomerFilter('');
                    setExcludedCustomers(new Set());
                  } else {
                    handleHeaderSortClick('customer', e);
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('ผู้ว่าจ้าง', 'Customer')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {customerFilter || excludedCustomers.size > 0
                      ? null
                      : sortIcon('customer')}
                  </Box>
                </Box>
                <ResizeHandle onStart={(e) => startResize(5, e)} />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('name', TH_SX),
                  width: getW(4),
                  minWidth: getW(4),
                  maxWidth: getW(4),
                  ...frzTh(4),
                  ...((projectFilter || excludedProjects.size > 0) && {
                    bgcolor: '#7c3aed',
                    color: '#fff',
                    '&:hover': { bgcolor: '#6d28d9' },
                  }),
                }}
                onClick={(e) => {
                  if (projectFilter || excludedProjects.size > 0) {
                    setProjectFilter('');
                    setExcludedProjects(new Set());
                  } else {
                    handleHeaderSortClick('name', e);
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('โครงการ', 'Project')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {projectFilter || excludedProjects.size > 0
                      ? null
                      : sortIcon('name')}
                  </Box>
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(4, e);
                  }}
                />
              </TableCell>
              {showGroup1 && (
                <>
                  <TableCell
                    sx={{
                      ...thSortSx('oldest_job_month', TH_SX),
                      width: getW(37),
                      minWidth: getW(37),
                      maxWidth: getW(37),
                      ...((monthFilter !== '' || excludedMonths.size > 0) && {
                        bgcolor: '#7c3aed',
                        color: '#fff',
                        '&:hover': { bgcolor: '#6d28d9' },
                      }),
                    }}
                    onClick={(e) => {
                      if (monthFilter !== '' || excludedMonths.size > 0) {
                        setMonthFilter('');
                        setExcludedMonths(new Set());
                      } else {
                        handleHeaderSortClick('oldest_job_month', e);
                      }
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>Mo</Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                        {monthFilter !== '' || excludedMonths.size > 0
                          ? null
                          : sortIcon('oldest_job_month')}
                      </Box>
                    </Box>
                    <ResizeHandle
                      onStart={(e) => {
                        e.stopPropagation();
                        startResize(37, e);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...thSortSx('oldest_job_year', TH_SX),
                      width: getW(38),
                      minWidth: getW(38),
                      maxWidth: getW(38),
                      ...((yearFilter !== '' || excludedYears.size > 0) && {
                        bgcolor: '#7c3aed',
                        color: '#fff',
                        '&:hover': { bgcolor: '#6d28d9' },
                      }),
                    }}
                    onClick={(e) => {
                      if (yearFilter !== '' || excludedYears.size > 0) {
                        setYearFilter('');
                        setExcludedYears(new Set());
                      } else {
                        handleHeaderSortClick('oldest_job_year', e);
                      }
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>Yr</Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                        {yearFilter !== '' || excludedYears.size > 0
                          ? null
                          : sortIcon('oldest_job_year')}
                      </Box>
                    </Box>
                    <ResizeHandle
                      onStart={(e) => {
                        e.stopPropagation();
                        startResize(38, e);
                      }}
                    />
                  </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  ...thSortSx('po', TH_SX),
                  ...TH_HAS_TOT,
                  width: getW(6),
                  minWidth: getW(6),
                  maxWidth: getW(6),
                  ...frzTh(6),
                }}
                onClick={(e) => handleHeaderSortClick('po', e)}
              >
                <Stack
                  direction="row"
                  spacing={0.4}
                  alignItems="center"
                  justifyContent="center"
                >
                  <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                    1{' '}
                  </span>
                  <span>PO</span>
                  {sortIcon('po')}
                </Stack>
                <Box sx={TH_TOT}>{fmt(totals.po)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(6, e);
                  }}
                />
              </TableCell>
              {/* col 7 วางบิลแล้วรอรับเงิน — แสดงเสมอ ไม่ผูกกับ toggle แสดง/ซ่อน Col */}
                  <TableCell
                    sx={{
                      ...thSortSx('billed_waiting', TH_GROUP_SX, '#6ba8a4'),
                      ...TH_HAS_TOT,
                      width: getW(7),
                      ...(statusFilter === 'col7_positive' && {
                        bgcolor: '#7c3aed',
                        color: '#fff',
                      }),
                    }}
                    onClick={(e) => handleHeaderSortClick('billed_waiting', e)}
                  >
                    <Stack
                      direction="row"
                      spacing={0.4}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                        2{' '}
                      </span>
                      <span>
                        Invoiced
                        <br />
                        No VAT
                      </span>
                      {sortIcon('billed_waiting')}
                    </Stack>
                    <Box sx={TH_TOT}>{fmt(totals.billed)}</Box>
                    <ResizeHandle
                      onStart={(e) => {
                        e.stopPropagation();
                        startResize(7, e);
                      }}
                    />
                  </TableCell>
              {showGroup1 && (
                <>
                  <TableCell
                    sx={{
                      ...thSortSx('received', TH_GROUP_SX, '#6ba8a4'),
                      ...TH_HAS_TOT,
                      width: getW(8),
                    }}
                    onClick={(e) => handleHeaderSortClick('received', e)}
                  >
                    <Stack
                      direction="row"
                      spacing={0.4}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                        3{' '}
                      </span>
                      {/* <span>ยังไม่ได้ส่งงาน</span> */}
                      <span>{L('รับเงินมาแล้ว', 'Received')}</span>
                      {sortIcon('received')}
                    </Stack>
                    <Box sx={TH_TOT}>{fmt(totals.received)}</Box>
                    <ResizeHandle
                      onStart={(e) => {
                        e.stopPropagation();
                        startResize(8, e);
                      }}
                    />
                  </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  ...thSortSx('po_remaining', TH_GROUP_SX, '#6ba8a4'),
                  ...TH_HAS_TOT,
                  width: getW(9),
                }}
                onClick={(e) => handleHeaderSortClick('po_remaining', e)}
              >
                <Stack
                  direction="row"
                  spacing={0.4}
                  alignItems="center"
                  justifyContent="center"
                >
                  <span style={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    fontSize: '0.55rem',
                    opacity: 0.55,
                    lineHeight: 1,
                    pointerEvents: 'none',
                    display: showColNums ? 'inline' : 'none',
                  }}>
                    4{' '}
                  </span>
                  <span>{L('ยอดค้างส่งงานจาก PO', 'PO Remaining')}</span>
                  {sortIcon('po_remaining')}
                </Stack>
                <Box
                  sx={{
                    ...TH_TOT,
                    // ตอนคอลัมน์นี้ถูก exclude-zero filter (พื้นหลังม่วง) ตัวหนังสือแดงอ่านแทบไม่
                    // ออกบนพื้นม่วง — สลับเป็นสีขาวตอนนั้นแทน
                    color: excludedZeroFields.has('po_remaining')
                      ? '#fff'
                      : totals.poRemain < 0
                      ? '#b91c1c'
                      : '#082f49',
                  }}
                >
                  {fmt(totals.poRemain)}
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(9, e);
                  }}
                />
              </TableCell>
              {showGroup1 && (
                <>
                  <TableCell
                    sx={{
                      ...thSortSx('target_profit', TH_GROUP4_SX, '#a7f3d0'),
                      ...TH_HAS_TOT,
                      width: getW(18),
                    }}
                    onClick={(e) => handleHeaderSortClick('target_profit', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                            position: 'absolute',
                            top: '50%',
                            right: 8,
                            transform: 'translateY(-50%)',
                            fontSize: '0.55rem',
                            opacity: 0.55,
                            lineHeight: 1,
                            pointerEvents: 'none',
                            display: showColNums ? 'inline' : 'none',
                          }}>
                          5{' '}
                        </span>
                        Profit Target
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('target_profit')}</Box>
                    </Box>
                    <Box sx={TH_TOT}>{fmt(totals.targetProfit)}</Box>
                    <ResizeHandle
                      onStart={(e) => {
                        e.stopPropagation();
                        startResize(18, e);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...thSortSx('total_budget', TH_GROUP2_SX, '#f9c4af'),
                      ...TH_HAS_TOT,
                      width: getW(33),
                    }}
                    onClick={(e) => handleHeaderSortClick('total_budget', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                            position: 'absolute',
                            top: '50%',
                            right: 8,
                            transform: 'translateY(-50%)',
                            fontSize: '0.55rem',
                            opacity: 0.55,
                            lineHeight: 1,
                            pointerEvents: 'none',
                            display: showColNums ? 'inline' : 'none',
                          }}>
                          6{' '}
                        </span>
                        {L('BG ที่ตั้งไว้', 'BG Set')}
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('total_budget')}</Box>
                    </Box>
                    <Box sx={TH_TOT}>{fmt(totals.budget)}</Box>
                    <ResizeHandle
                      onStart={(e) => {
                        e.stopPropagation();
                        startResize(33, e);
                      }}
                    />
                  </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  ...thSortSx('total_expenses', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(11),
                }}
                onClick={(e) => handleHeaderSortClick('total_expenses', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      7{' '}
                    </span>
                    PAID
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('total_expenses')}
                  </Box>
                </Box>
                <Box sx={TH_TOT}>{fmt(totals.spent)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(11, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('projected_fund', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(10),
                }}
                onClick={(e) => handleHeaderSortClick('projected_fund', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      8{' '}
                    </span>
                    {lang === 'th' ? (
                      'BG หักจ่ายแล้ว'
                    ) : (
                      'BG - PAID'
                    )}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('projected_fund')}
                  </Box>
                </Box>
                <Box sx={TH_TOT}>{fmt(totals.projectedFund)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(10, e);
                  }}
                />
              </TableCell>
              {showGroup1 && (
                <>
              <TableCell
                sx={{
                  ...thSortSx('expected_value', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(35),
                }}
                onClick={(e) => handleHeaderSortClick('expected_value', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      9{' '}
                    </span>
                    <span>{L('รายการตั้งเบิก ที่รอจ่าย', 'Pending Expenses')}</span>
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('expected_value')}
                  </Box>
                </Box>
                <Box sx={TH_TOT}>{fmt(totals.expected)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(35, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('ap_pending_unpaid', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(32),
                }}
                onClick={(e) => handleHeaderSortClick('ap_pending_unpaid', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      10{' '}
                    </span>
                    <span>{L('AP PENDING', 'AP Pending')}</span>
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('ap_pending_unpaid')}
                  </Box>
                </Box>
                <Box sx={TH_TOT}>{fmt(totals.apPendingUnpaid)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(32, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('to_spend', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(16),
                }}
                onClick={(e) => handleHeaderSortClick('to_spend', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      11{' '}
                    </span>
                    {L('ต้องใช้อีก', 'To Spend')}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('to_spend')}</Box>
                </Box>
                <Box sx={TH_TOT}>{fmt(totals.col16)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(16, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('col12_payable_bg_eligible', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(12),
                }}
                onClick={(e) => handleHeaderSortClick('col12_payable_bg_eligible', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      12{' '}
                    </span>
                    {L('BGเหลือที่มีสิทธิจ่าย', 'BG Eligible')}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('col12_payable_bg_eligible')}
                  </Box>
                </Box>
                <Box sx={TH_TOT}>{fmt(totals.col12)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(12, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('refund_pending', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(34),
                }}
                onClick={(e) => handleHeaderSortClick('refund_pending', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      13{' '}
                    </span>
                    <Tooltip title="รอเงินคืน (เช่นค่ามัดจำ)">
                      <span>{L('รอเงินคืน (เช่นค่ามัดจำ)', 'Refund Pending')}</span>
                    </Tooltip>
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('refund_pending')}
                  </Box>
                </Box>
                <Box
                  sx={{
                    ...TH_TOT,
                    color: '#059669',
                  }}
                >
                  {fmt(totals.refundPending)}
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(34, e);
                  }}
                />
              </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  ...thSortSx('col17', TH_GROUP2_SX, '#f9c4af'),
                  ...TH_HAS_TOT,
                  width: getW(17),
                }}
                onClick={(e) => handleHeaderSortClick('col17', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      14{' '}
                    </span>
                    {L('สรุปอนาคตต้องจ่ายอีก', 'Further Payable')}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('col17')}</Box>
                </Box>
                <Box
                  sx={{
                    ...TH_TOT,
                    // ตอนคอลัมน์นี้ถูก exclude-zero filter (พื้นหลังม่วง) ตัวหนังสือแดงอ่านแทบไม่
                    // ออกบนพื้นม่วง — สลับเป็นสีขาวตอนนั้นแทน
                    color: excludedZeroFields.has('col17')
                      ? '#fff'
                      : totals.col17 > 0
                      ? '#b91c1c'
                      : '#082f49',
                  }}
                >
                  {fmt(totals.col17)}
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(17, e);
                  }}
                />
              </TableCell>
              {showGroup1 && (
                <>
                  <TableCell
                    sx={{ ...thSortSx('delivery_date', TH_GROUP5_SX, '#e9d5ff'), width: getW(27) }}
                    onClick={(e) => handleHeaderSortClick('delivery_date', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                          15{' '}
                        </span>
                        <Tooltip title="ส่งงานวันที่ / Delivery Date">
                          <span>{L('ส่งงานวันที่', 'Delivery')}</span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('delivery_date')}</Box>
                    </Box>
                    <ResizeHandle onStart={(e) => startResize(27, e)} />
                  </TableCell>
                  <TableCell
                    sx={{ ...thSortSx('credit_days', TH_GROUP5_SX, '#e9d5ff'), width: getW(28) }}
                    onClick={(e) => handleHeaderSortClick('credit_days', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                          16{' '}
                        </span>
                        <Tooltip title="เครดิต (วัน) / Credit (Days)">
                          <span>{L('เครดิต (วัน)', 'Credit')}</span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('credit_days')}</Box>
                    </Box>
                    <ResizeHandle onStart={(e) => startResize(28, e)} />
                  </TableCell>
                  <TableCell
                    sx={{ ...thSortSx('payment_cycle_day', TH_GROUP5_SX, '#e9d5ff'), width: getW(31) }}
                    onClick={(e) => handleHeaderSortClick('payment_cycle_day', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                          17{' '}
                        </span>
                        <Tooltip title="รอบจ่ายเงิน / Payment Cycle">
                          <span>{L('รอบจ่ายเงิน', 'Cycle')}</span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('payment_cycle_day')}</Box>
                    </Box>
                    <ResizeHandle onStart={(e) => startResize(31, e)} />
                  </TableCell>
                  <TableCell
                    sx={{ ...thSortSx('expected_date', TH_GROUP5_SX, '#e9d5ff'), width: getW(29) }}
                    onClick={(e) => handleHeaderSortClick('expected_date', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                          18{' '}
                        </span>
                        <Tooltip title="รับเงินวันที่ / Receipt Date">
                          <span>{L('รับเงินวันที่', 'Receipt')}</span>
                        </Tooltip>
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('expected_date')}</Box>
                    </Box>
                    <ResizeHandle onStart={(e) => startResize(29, e)} />
                  </TableCell>
                  <TableCell
                    sx={{ ...thSortSx('days_left', TH_GROUP5_SX, '#e9d5ff'), width: getW(30) }}
                    onClick={(e) => handleHeaderSortClick('days_left', e)}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box>
                        <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                          19{' '}
                        </span>
                        {L('รับเงินอีก(วัน)', 'Days Left')}
                      </Box>
                      <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('days_left')}</Box>
                    </Box>
                    <ResizeHandle onStart={(e) => startResize(30, e)} />
                  </TableCell>
                </>
              )}
              <TableCell
                sx={{
                  ...thSortSx('net_remaining', TH_GROUP3_SX, '#60a5fa'),
                  ...TH_HAS_TOT,
                  borderTop: '6px solid #2563eb',
                  width: getW(15),
                }}
                onClick={(e) => handleHeaderSortClick('net_remaining', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      20{' '}
                    </span>
                    {L('สรุป BG คงเหลือ', 'BG Remaining')}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {sortIcon('net_remaining')}
                  </Box>
                </Box>
                <Box sx={{ ...TH_TOT, color: '#16a34a' }}>{fmt(totals.col15)}</Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(15, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('over_budget', TH_GROUP3_SX, '#60a5fa'),
                  ...TH_HAS_TOT,
                  borderTop: '6px solid #2563eb',
                  width: getW(14),
                }}
                onClick={(e) => handleHeaderSortClick('over_budget', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      21{' '}
                    </span>
                    {L('สรุป BG เกิน', 'Over Budget')}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('over_budget')}</Box>
                </Box>
                {totals.col14 < 0 && (
                  <Box
                    sx={{
                      ...TH_TOT,
                      color: excludedZeroFields.has('over_budget') ? '#fff' : '#b91c1c',
                    }}
                  >
                    {fmt(totals.col14)}
                  </Box>
                )}
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(14, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...TH_GROUP3_SX,
                  ...TH_HAS_TOT,
                  width: getW(19),
                  cursor: 'pointer',
                  userSelect: 'none',
                  ...(getSortDir('realprofit') !== null && {
                    bgcolor: '#c09614',
                    color: '#fff',
                    borderBottom: '3px solid #d4a017',
                  }),
                  '&:hover': {
                    bgcolor:
                      getSortDir('realprofit') !== null
                        ? '#9b780f'
                        : '#fef08a',
                  },
                }}
                onClick={(e) => handleHeaderSortClick('realprofit', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>
                    <span style={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        transform: 'translateY(-50%)',
                        fontSize: '0.55rem',
                        opacity: 0.55,
                        lineHeight: 1,
                        pointerEvents: 'none',
                        display: showColNums ? 'inline' : 'none',
                      }}>
                      22{' '}
                    </span>
                    {L('สรุปกำไรคาดการณ์', 'Est. Profit')}
                  </Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>
                    {getSortDir('realprofit') === 'asc' ? (
                      <ArrowDropUpIcon
                        sx={{
                          fontSize: 40,
                          color: '#ef4444',
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      />
                    ) : getSortDir('realprofit') === 'desc' ? (
                      <ArrowDropDownIcon
                        sx={{
                          fontSize: 40,
                          color: '#ef4444',
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      />
                    ) : null}
                  </Box>
                  {lang !== 'th' && showColNums && (
                    <Box
                      sx={{
                        fontSize: '0.73rem',
                        opacity: 0.65,
                        lineHeight: 1.2,
                        mt: 0.3,
                      }}
                    >
                      (1−7−14)
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    ...TH_TOT,
                    // ตอนคอลัมน์นี้ถูก exclude-zero filter (พื้นหลังม่วง) ตัวหนังสือแดงอ่านแทบไม่
                    // ออกบนพื้นม่วง — สลับเป็นสีขาวตอนนั้นแทน
                    color: excludedZeroFields.has('realprofit')
                      ? '#fff'
                      : totals.profit < 0
                      ? '#b91c1c'
                      : '#082f49',
                  }}
                >
                  {fmt(totals.profit)}
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(19, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('bg_used_pct', TH_GROUP3_SX, '#fde68a'),
                  width: getW(13),
                }}
                onClick={(e) => handleHeaderSortClick('bg_used_pct', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('%ใช้ BG', '% BG Used')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('bg_used_pct')}</Box>
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(13, e);
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  ...thSortSx('profit_pct', TH_GROUP3_SX, '#fde68a'),
                  width: getW(20),
                }}
                onClick={(e) => handleHeaderSortClick('profit_pct', e)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('% กำไร', '% Profit')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('profit_pct')}</Box>
                </Box>
                <ResizeHandle
                  onStart={(e) => {
                    e.stopPropagation();
                    startResize(20, e);
                  }}
                />
              </TableCell>
              <TableCell sx={{ ...TH_SX, width: getW(26) }}>
                {L('หมายเหตุ', 'Notes')}
                <ResizeHandle onStart={(e) => startResize(26, e)} />
              </TableCell>
              {(onEditProject || onDeleteProject) && (
                <TableCell
                  sx={{
                    ...TH_SX,
                    width: getW(39),
                    minWidth: getW(39),
                    textAlign: 'center',
                  }}
                >
                  {L('จัดการ', 'Manage')}
                  <ResizeHandle onStart={(e) => startResize(39, e)} />
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{ opacity: isStale ? 0.55 : 1, transition: 'opacity 0.18s' }}
          >
            {isLoading &&
              rows.length === 0 &&
              Array.from({ length: 10 }, (_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: showGroup1 ? 27 : 9 }, (_, j) => (
                    <TableCell key={j} sx={{ py: 0.8 }}>
                      <Skeleton
                        animation="wave"
                        height={18}
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {displayRows.map((row, idx) => {
              // ผู้ว่าจ้างซ้ำกับแถวก่อนหน้าติดกัน (ไม่ว่าจะเรียงด้วยอะไรอยู่ก็ตาม) — โชว์ชื่อแค่แถวแรก
              // ของกลุ่มที่ติดกัน แถวถัดๆ ไปเว้นว่าง (คลิก/คลิกขวา filter ยังใช้ค่าจริงได้ปกติ)
              const sameCustomerAsPrev =
                idx > 0 &&
                getCustomerName(row) === getCustomerName(displayRows[idx - 1]);
              // โปรเจกต์ฟ้องร้องถูก sort ไว้ล่างสุดเสมอ (ดูจุด sort ด้านบน) — คั่นแถบระหว่าง
              // กลุ่มปกติกับกลุ่มฟ้องร้องให้เห็นชัดว่าเป็นคนละกลุ่มกัน ไม่ใช่แค่ปนกันมาเรียงต่อ
              const groupOf = (r) => rowGroup(r, includeCompletedCommitted);
              const isFirstLawsuitRow =
                groupOf(row) === 2 &&
                (idx === 0 || groupOf(displayRows[idx - 1]) !== 2);
              const isFirstJudgedRow =
                groupOf(row) === 3 &&
                (idx === 0 || groupOf(displayRows[idx - 1]) !== 3);
              const isFirstPaidCommittedRow =
                rowGroup(row, includeCompletedCommitted) === 1 &&
                (idx === 0 ||
                  rowGroup(displayRows[idx - 1], includeCompletedCommitted) !== 1);
              return (
                <React.Fragment key={row._id}>
                  {isFirstPaidCommittedRow && (
                    <>
                      <LawsuitDividerRow title="ส่งงานครบแล้ว และยังมีภาระผูกพัน" />
                      {renderGroupTotalsRow(
                        groupTotals.paid,
                        '#dcfce7',
                        'โครงการที่ส่งงานครบแล้ว และยังมีภาระผูกพัน',
                        '#166534',
                      )}
                    </>
                  )}
                  {isFirstLawsuitRow && (
                    <>
                      <LawsuitDividerRow bold />
                      {renderGroupTotalsRow(
                        groupTotals.lawsuit,
                        '#fff1f1',
                        'โครงการที่บริษัทดำเนินการฟ้องร้องผู้ว่าจ้าง',
                        '#991b1b',
                      )}
                    </>
                  )}
                  {isFirstJudgedRow && (
                    <>
                      <LawsuitDividerRow title="พิพากษาแล้ว" />
                      {renderGroupTotalsRow(
                        groupTotals.judged,
                        '#fdf2e3',
                        'โครงการที่ศาลพิพากษาแล้ว',
                        '#78350f',
                      )}
                    </>
                  )}
                  <ProjectDashboardRow
                  row={row}
                  idx={idx}
                  mergedRow={row}
                  showCol2={showCol2}
                  showGroup1={showGroup1}
                  projectFilter={projectFilter}
                  customerFilter={customerFilter}
                  stickyLeftOf={stickyLeftOf}
                  getW={getW}
                  getVal={getVal}
                  getCustomerName={getCustomerName}
                  sameCustomerAsPrev={sameCustomerAsPrev}
                  onClickProject={onClickProject}
                  scheduleClick={scheduleClick}
                  cancelAndRun={cancelAndRun}
                  setProjectFilter={setProjectFilter}
                  setCustomerFilter={setCustomerFilter}
                  onToggleExcludeProject={toggleExcludedProject}
                  onToggleExcludeCustomer={toggleExcludedCustomer}
                  monthFilter={monthFilter}
                  yearFilter={yearFilter}
                  setMonthFilter={setMonthFilter}
                  setYearFilter={setYearFilter}
                  onToggleExcludeMonth={toggleExcludedMonth}
                  onToggleExcludeYear={toggleExcludedYear}
                  onToggleZeroExclude={toggleZeroExclude}
                  renderSeedCell={renderSeedCell}
                  todayReviewStr={todayReviewStr}
                  aiQuestionCount={aiQuestionCounts[String(row._id)] || 0}
                  isPinned={row._id === pinnedProjectId}
                  paidCommittedIncluded={includeCompletedCommitted}
                  isPaidCommitted={
                    !row.legal_lawsuit &&
                    !row.legal_judged &&
                    isFullyPaid(row) &&
                    computeHasCommitment(row)
                  }
                  onOpenActionRequired={openActionDialog}
                  onEditProject={onEditProject}
                  onDeleteProject={onDeleteProject}
                  />
                </React.Fragment>
              );
            })}

            {displayRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={showGroup1 ? 31 : 13}
                  sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}
                >
                  ไม่พบข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <FilterPopover
        open={filterOpen}
        anchorEl={containerRef.current}
        onClose={() => setFilterOpen(false)}
      >
        {statusSummaryChips}
      </FilterPopover>

      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Dialog open={!!actionDialog} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog?.mode === 'remove'
            ? 'ปลดป้าย Action required'
            : 'เพิ่มป้าย Action required'}
        </DialogTitle>
        <DialogContent>
          {actionDialog?.mode === 'remove' ? (
            <>
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: '#fef2f2',
                  color: '#991b1b',
                  fontSize: '0.8rem',
                }}
              >
                <Box sx={{ fontWeight: 700 }}>
                  {actionDialog?.row?.action_required?.text}
                </Box>
                <Box sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                  บันทึกโดย {actionDialog?.row?.action_required?.createdBy || 'KPP'} ·{' '}
                  {actionDialog?.row?.action_required?.createdAt
                    ? dayjs(actionDialog.row.action_required.createdAt).format('DD/MM/YYYY HH:mm')
                    : '-'}
                </Box>
              </Box>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                type="password"
                name="action-required-close-password"
                autoComplete="new-password"
                label="Password สำหรับปิดป้าย"
                value={actionPassword}
                onChange={(e) => setActionPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitActionDialog();
                }}
              />
            </>
          ) : (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              label="Action required"
              value={actionText}
              inputProps={{ maxLength: 500 }}
              onChange={(e) => setActionText(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={actionSaving}>ยกเลิก</Button>
          <Button
            variant="contained"
            color={actionDialog?.mode === 'remove' ? 'error' : 'warning'}
            disabled={
              actionSaving ||
              (actionDialog?.mode === 'remove'
                ? !actionPassword
                : !actionText.trim())
            }
            onClick={submitActionDialog}
          >
            {actionDialog?.mode === 'remove' ? 'ปิดป้าย' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ProjectDashboardTable;
